import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Switch } from '@/components/ui/switch'
import { SubscriptionBlocker } from '@/components/SubscriptionBlocker'
import { useAuthStore } from '@/stores/auth.store'
import ixenSystemService, { IxenPair, IxenRiskState, IxenSignal, PairCreateRequest, YomiSignalResponse, YomiLastSignalResponse, SempaiAlertItem, MakatsuSignalItem, MarketContextResponse, SessionQualityData } from '@/services/ixen_system.service'
import { useZanshinData } from '@/contexts/ZanshinContext'
import { ZanshinPairCard } from '@/components/zanshin/ZanshinPairCard'
import { ZanshinApprovalsPanel } from '@/components/zanshin/ZanshinApprovalsPanel'
import { zanshinService, ZanshinMode } from '@/services/zanshin.service'
import { PairManagementModal } from '@/components/PairManagementModal'

// ─── Stili base ───────────────────────────────────────────────────────────────
const card = 'bg-[#0d1529] border border-[#1e3a5f] rounded-xl p-4'
const sectionTitle = 'text-[11px] font-bold tracking-widest uppercase text-[#64748b] mb-3 flex items-center gap-2 before:content-["•"] before:text-[#38bdf8] before:text-base'
const inputCls = 'w-full bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none focus:border-[#38bdf8] transition-colors'
const labelCls = 'block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1'

const PROVIDERS = [
  { value: 'FND', label: 'FundedNext' },
  { value: 'FPI', label: 'FundingPips' },
  { value: 'FPR', label: 'FunderPro' },
  { value: 'FP2', label: 'FundingPips 2 Step' },
]

const PROP_SIZES = [25000, 50000, 100000, 150000, 200000]
// FP2: solo tagli ufficiali FundingPips 2 Step
const PROP_SIZES_FP2 = [50000, 100000, 200000]

const PHASES = [
  { value: 'challenge', label: 'Challenge' },
  { value: 'real_pre_payout', label: 'Pre Payout' },
  { value: 'real_post_payout', label: 'Post Payout' },
]
// FP2 ha fasi proprie
const PHASES_FP2 = [
  { value: 'one_step', label: 'One Step' },
  { value: 'two_step', label: 'Two Step' },
  { value: 'master', label: 'Master' },
]

export function phasesForProvider(provider: string) {
  return provider === 'FP2' ? PHASES_FP2 : PHASES
}
export function sizesForProvider(provider: string) {
  return provider === 'FP2' ? PROP_SIZES_FP2 : PROP_SIZES
}

const SL_MAX_BY_PROVIDER: Record<string, number> = {
  FND: 0.012,
  FPI: 0.012,
  FPR: 0.012,
  FP2: 0.012,
}

// FunderPro margin mode (solo per Challenge)
const FPR_MARGIN_MAP: Record<string, number> = { prudente: 0.65, standard: 0.80, massimale: 0.95 }
type FprMarginMode = 'prudente' | 'standard' | 'massimale'

// Calcolo SL max dinamico: min(cap provider, buffer equity reale)
function calcDynamicSlMax(equity: number | null, propSize: number, providerMax: number): number {
  const MIN_SL = 0.002
  if (!equity || equity <= 0 || propSize <= 0) return providerMax
  const minEquity = propSize * 0.94
  const bufferDollar = Math.max(0, equity - minEquity)
  const bufferPercent = bufferDollar / propSize
  return Math.max(MIN_SL, Math.min(providerMax, bufferPercent))
}

const TP_MAX_BY_PROVIDER_PHASE: Record<string, Record<string, number>> = {
  FND: { challenge: 0.06, real_pre_payout: 0.02, real_post_payout: 0.02 },
  // "real" è la chiave legacy/compat per FPI (alcune coppie hanno phase="real" puro)
  FPI: { challenge: 0.04, real: 0.035, real_pre_payout: 0.035, real_post_payout: 0.035 },
  FPR: { challenge: 0.04, real_pre_payout: 0.02, real_post_payout: 0.02 },
  // FP2: TP max 1.2% in tutte le fasi
  FP2: { one_step: 0.012, two_step: 0.012, master: 0.012 },
}

function getSlTpLimits(provider: string, phase: string, controlMode = false) {
  const slMax = SL_MAX_BY_PROVIDER[provider] ?? 0.012
  const fndRealControl = controlMode
    && provider === 'FND'
    && ['real', 'real_pre_payout', 'real_post_payout'].includes(phase)
  const tpMax = fndRealControl
    ? 0.03
    : (TP_MAX_BY_PROVIDER_PHASE[provider]?.[phase] ?? 0.04)
  return { slMax, tpMax }
}

// ─── Componenti UI ────────────────────────────────────────────────────────────

function SpreadBadge({ pts }: { pts: number | null }) {
  if (pts === null) return <span className="text-[#475569] text-xs">—</span>
  if (pts > 90) return <span className="text-[10px] font-bold bg-red-900/40 text-red-400 px-2 py-0.5 rounded-full">{pts.toFixed(0)}pt BLOCCO</span>
  if (pts > 30) return <span className="text-[10px] font-bold bg-yellow-900/40 text-yellow-400 px-2 py-0.5 rounded-full">{pts.toFixed(0)}pt ⚠</span>
  return <span className="text-[10px] font-bold bg-green-900/40 text-green-400 px-2 py-0.5 rounded-full">{pts.toFixed(0)}pt</span>
}

type EaState = 'READY' | 'NOT_READY' | 'DISCONNECTED'

function getEaState(ea: { connected: boolean; trade_allowed: boolean | null }): EaState {
  if (!ea.connected) return 'DISCONNECTED'
  if (ea.trade_allowed === false) return 'NOT_READY'
  return 'READY'
}

const NOT_READY_LABELS: Record<string, string> = {
  AUTOTRADING_OFF: 'AutoTrading OFF',
  ACCOUNT_NOT_ALLOWED: 'Account bloccato',
  SYMBOL_NOT_TRADEABLE: 'Simbolo non tradeable',
}

function EaBadge({ state, type, reason }: { state: EaState; type: string; reason?: string | null }) {
  const dot = state === 'READY'
    ? 'bg-green-400 shadow-[0_0_6px_#4ade80]'
    : state === 'NOT_READY'
    ? 'bg-yellow-400 shadow-[0_0_6px_#facc15]'
    : 'bg-red-500'
  const label = state === 'READY' ? 'CONNESSO' : state === 'NOT_READY' ? 'NON PRONTO' : 'OFFLINE'
  const labelColor = state === 'READY' ? 'text-green-400' : state === 'NOT_READY' ? 'text-yellow-400' : 'text-red-400'
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
      <span className="text-[10px] font-mono text-[#94a3b8]">{type}</span>
      <span className={`text-[9px] font-bold ${labelColor}`}>{label}</span>
      {state === 'NOT_READY' && reason && (
        <span className="text-[9px] text-yellow-500/80">{NOT_READY_LABELS[reason] ?? reason}</span>
      )}
    </div>
  )
}

function PhaseBadge({ label }: { label: string }) {
  return <span className="text-[10px] bg-[#1e3a5f] text-[#38bdf8] px-1.5 py-0.5 rounded font-bold">{label}</span>
}

// FASE 1 — badge stato rischio card. Mostra in ordine di gravità:
// card disattivata (limite giornaliero / margine broker) → chiusura auto T-1h →
// blocco aperture auto T-3h → consumo daily informativo. Nasconde se tutto ok.
function RiskBadge({ rs }: { rs?: IxenRiskState | null }) {
  if (!rs || !rs.enabled) return null
  const pillBase = 'text-[9px] font-black px-1.5 py-0.5 rounded tracking-normal normal-case'
  if (rs.card_blocked) {
    const reason = rs.daily_locked
      ? `limite giornaliero ${rs.daily_consumed_pct != null ? (rs.daily_consumed_pct * 100).toFixed(1) + '%' : ''}`.trim()
      : 'margine broker insufficiente'
    return (
      <span className={`${pillBase} text-rose-300 bg-rose-500/15 border border-rose-500/50`} title={`Card disattivata: ${reason}`}>
        🚫 BLOCCATA · {rs.daily_locked ? 'daily' : 'margine'}
      </span>
    )
  }
  if (rs.force_close_window) {
    return (
      <span className={`${pillBase} text-orange-300 bg-orange-500/15 border border-orange-500/50`} title="Chiusura automatica delle operazioni auto in corso (T-1h a fine giornata UTC)">
        ⏱ Chiusura auto T-1h
      </span>
    )
  }
  if (rs.auto_open_blocked_window) {
    return (
      <span className={`${pillBase} text-amber-300 bg-amber-500/15 border border-amber-500/50`} title="Nuove aperture automatiche sospese (T-3h a fine giornata UTC 00:00)">
        ⏸ Auto sospeso T-3h
      </span>
    )
  }
  if (rs.daily_consumed_pct != null && rs.daily_consumed_pct > 0) {
    const pct = (rs.daily_consumed_pct * 100).toFixed(1)
    const lim = (rs.daily_limit_pct * 100).toFixed(1)
    return (
      <span className={`${pillBase} text-[#94a3b8] bg-[#1e293b] border border-[#334155]`} title={`Perdita netta giornaliera ${pct}% (limite ${lim}%)`}>
        Daily −{pct}%
      </span>
    )
  }
  return null
}

// Bilancio provvisorio di giornata (P&L REALIZZATO dalla mezzanotte UTC):
// netto % con segno, verde se >= 0, rosso se < 0. Sempre visibile (a differenza
// del RiskBadge che mostra solo gli stati d'allarme). Dettaglio $ + residuo perdita
// nel tooltip.
function DailyBalanceBadge({ rs }: { rs?: IxenRiskState | null }) {
  if (!rs || !rs.enabled || rs.daily_net_pct == null) return null
  const net = rs.daily_net_pct
  const up = net >= 0
  const pctStr = `${up ? '+' : '−'}${Math.abs(net * 100).toFixed(2)}%`
  const d = rs.daily_net_dollars
  const dollarStr = d != null
    ? `${d >= 0 ? '+' : '−'}${Math.abs(d).toLocaleString('it-IT', { maximumFractionDigits: 0 })} $`
    : ''
  const room = rs.daily_room_pct != null ? (rs.daily_room_pct * 100).toFixed(2) : null
  const lim = (rs.daily_limit_pct * 100).toFixed(1)
  const cls = up
    ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/40'
    : 'text-rose-300 bg-rose-500/10 border border-rose-500/40'
  const resetApplied = !!rs.reset_applied
  const title = `Bilancio realizzato di giornata (${resetApplied ? 'dal reset di oggi' : 'dalla mezzanotte UTC'}): ${pctStr}`
    + (dollarStr ? ` (${dollarStr})` : '')
    + (room != null ? ` · residuo perdita ${room}% / ${lim}%` : '')
    + (resetApplied ? ' · daily-loss azzerato oggi (prop nuova)' : '')
  return (
    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded tracking-normal normal-case ${cls}`} title={title}>
      {resetApplied ? '↺ ' : ''}Giorno {pctStr}{dollarStr ? ` · ${dollarStr}` : ''}
    </span>
  )
}

function SignalStatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    PENDING: 'bg-yellow-900/40 text-yellow-400',
    EXECUTED: 'bg-green-900/40 text-green-400',
    FAILED: 'bg-red-900/40 text-red-400',
    BLOCKED: 'bg-red-900/40 text-red-400',
    CANCELLED: 'bg-slate-800 text-slate-400',
  }
  const labels: Record<string, string> = {
    PENDING: 'In attesa', EXECUTED: 'Eseguito',
    FAILED: 'Fallito', BLOCKED: 'Bloccato', CANCELLED: 'Annullato',
  }
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls[status] ?? 'bg-slate-800 text-slate-400'}`}>{labels[status] ?? status}</span>
}

// ─── Modal Crea Coppia ─────────────────────────────────────────────────────────

const EMPTY_FORM: PairCreateRequest = {
  display_name: '',
  prop_login: '',
  prop_password: '',
  prop_server: '',
  prop_size: 100000,
  prop_provider: 'FND',
  prop_phase: 'challenge',
  broker_login: '',
  broker_password: '',
  broker_server: '',
}

function CreatePairModal({ onClose, onCreated }: { onClose: () => void; onCreated: (p: IxenPair) => void }) {
  const [form, setForm] = useState<PairCreateRequest>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const set = (k: keyof PairCreateRequest, v: string | number) =>
    setForm(f => ({ ...f, [k]: v }))

  // Cambio provider: riallinea fase e capitale ai valori validi del provider
  const setProvider = (prov: string) =>
    setForm(f => {
      const phases = prov === 'FP2' ? PHASES_FP2 : PHASES
      const sizes = prov === 'FP2' ? PROP_SIZES_FP2 : PROP_SIZES
      return {
        ...f,
        prop_provider: prov,
        prop_phase: phases.some(p => p.value === f.prop_phase) ? f.prop_phase : phases[0].value,
        prop_size: sizes.includes(f.prop_size) ? f.prop_size : (sizes.includes(100000) ? 100000 : sizes[0]),
        fp2_risk_profile: prov === 'FP2' ? (f.fp2_risk_profile ?? 'conservativo') : undefined,
      }
    })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.prop_login.trim()) return toast.error('Login PROP obbligatorio')
    if (!form.prop_server.trim()) return toast.error('Server PROP obbligatorio')
    if (!form.broker_login.trim()) return toast.error('Login BROKER obbligatorio')
    if (!form.broker_server.trim()) return toast.error('Server BROKER obbligatorio')

    setSaving(true)
    try {
      const pair = await ixenSystemService.createPair({
        ...form,
        display_name: form.display_name?.trim() || undefined,
      })
      toast.success('Coppia EA creata', { description: 'Token EA generati automaticamente.' })
      onCreated(pair)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? 'Errore creazione coppia')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#0d1529] border border-[#1e3a5f] rounded-2xl p-6 w-full max-w-lg shadow-2xl my-4">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-black text-white text-base">Nuova coppia EA</h2>
            <p className="text-[11px] text-[#475569] mt-0.5">Nessun account MetaAPI richiesto</p>
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-white text-lg font-bold transition-colors">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Nome visualizzato */}
          <div>
            <label className={labelCls}>Nome visualizzato <span className="text-[#334155] normal-case font-normal">(opzionale)</span></label>
            <input
              className={inputCls}
              placeholder="es. FundedNext 100K Challenge"
              value={form.display_name}
              onChange={e => set('display_name', e.target.value)}
            />
          </div>

          {/* ── PROP ── */}
          <div className="bg-[#0a0f1e] rounded-xl p-4 border border-[#1e3a5f]/50 space-y-3">
            <div className="text-[10px] font-black tracking-widest uppercase text-[#38bdf8]">MT5 — Conto PROP</div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Login (numero)</label>
                <input
                  className={inputCls}
                  placeholder="12345678"
                  value={form.prop_login}
                  onChange={e => set('prop_login', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Password</label>
                <input
                  type="password"
                  className={inputCls}
                  placeholder="••••••••"
                  value={form.prop_password}
                  onChange={e => set('prop_password', e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Server</label>
              <input
                className={inputCls}
                placeholder="ICMarkets-SC10"
                value={form.prop_server}
                onChange={e => set('prop_server', e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Provider</label>
                <select
                  className={inputCls}
                  value={form.prop_provider}
                  onChange={e => setProvider(e.target.value)}
                >
                  {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Capitale ($)</label>
                <select
                  className={inputCls}
                  value={form.prop_size}
                  onChange={e => set('prop_size', Number(e.target.value))}
                >
                  {sizesForProvider(form.prop_provider).map(s => <option key={s} value={s}>{s.toLocaleString()}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Fase</label>
                <select
                  className={inputCls}
                  value={form.prop_phase}
                  onChange={e => set('prop_phase', e.target.value)}
                >
                  {phasesForProvider(form.prop_provider).map(ph => <option key={ph.value} value={ph.value}>{ph.label}</option>)}
                </select>
              </div>
            </div>

            {form.prop_provider === 'FP2' && (
              <div>
                <label className={labelCls}>Profilo rischio <span className="text-[#334155] normal-case font-normal">(immutabile dopo la creazione)</span></label>
                <select
                  className={inputCls}
                  value={form.fp2_risk_profile ?? 'conservativo'}
                  onChange={e => set('fp2_risk_profile', e.target.value)}
                >
                  <option value="conservativo">Conservativo (copertura 17% / 34%)</option>
                  <option value="aggressivo">Aggressivo (copertura 20% / 40%)</option>
                </select>
                <p className="text-[10px] text-[#475569] mt-1">
                  Il profilo determina la copertura broker in One/Two Step (Master: 70% fisso) e non può essere cambiato in seguito.
                </p>
              </div>
            )}
          </div>

          {/* ── BROKER ── */}
          <div className="bg-[#0a0f1e] rounded-xl p-4 border border-[#1e3a5f]/50 space-y-3">
            <div className="text-[10px] font-black tracking-widest uppercase text-[#64748b]">MT4 — Conto BROKER</div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Login (numero)</label>
                <input
                  className={inputCls}
                  placeholder="98765432"
                  value={form.broker_login}
                  onChange={e => set('broker_login', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Password</label>
                <input
                  type="password"
                  className={inputCls}
                  placeholder="••••••••"
                  value={form.broker_password}
                  onChange={e => set('broker_password', e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Server</label>
              <input
                className={inputCls}
                placeholder="ICMarketsSC-MT4"
                value={form.broker_server}
                onChange={e => set('broker_server', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="bg-[#0a0f1e] rounded-lg p-3 border border-[#1e3a5f]/40">
            <p className="text-[10px] text-[#475569]">
              ⚡ I <strong className="text-[#64748b]">token EA</strong> vengono generati automaticamente dopo la creazione.
              Nessuna connessione MetaAPI viene stabilita — gli EA si collegano direttamente ai server del broker/prop.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-[#1e3a5f] text-[#64748b] text-sm font-bold hover:bg-[#1e3a5f]/30 transition-all"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] border border-[#38bdf8]/50 text-[#38bdf8] text-sm font-black hover:bg-[#1e4a7f] disabled:opacity-40 transition-all"
            >
              {saving ? 'Creazione...' : 'Crea coppia EA'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Modal Token EA ────────────────────────────────────────────────────────────

const IXEN_CLOUD_URL = 'https://ixen.info'

function TokenModal({ pair, type, token, onReset, onClose }: {
  pair: IxenPair; type: 'PROP' | 'BROKER'; token: string;
  onReset: () => void; onClose: () => void
}) {
  const [resetting, setResetting] = useState(false)
  const [tokenCopied, setTokenCopied] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)
  const login = type === 'PROP' ? pair.prop_login : pair.broker_login
  const server = type === 'PROP' ? pair.prop_server : pair.broker_server

  async function handleReset() {
    setResetting(true)
    try { await onReset() } finally { setResetting(false) }
  }

  function handleCopyToken() {
    navigator.clipboard.writeText(token)
    setTokenCopied(true)
    toast.success('Token copiato negli appunti')
    setTimeout(() => setTokenCopied(false), 2000)
  }

  function handleCopyUrl() {
    navigator.clipboard.writeText(IXEN_CLOUD_URL)
    setUrlCopied(true)
    toast.success('URL copiato negli appunti')
    setTimeout(() => setUrlCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0d1529] border border-[#1e3a5f] rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[#38bdf8] font-black text-sm">Token EA — {type === 'PROP' ? 'MT5 PROP' : 'MT4 BROKER'}</div>
            <div className="text-[10px] text-[#475569] mt-0.5">{pair.display_name} • Login {login} • {server}</div>
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-white text-lg font-bold">✕</button>
        </div>

        {/* Token */}
        <div className="mb-1">
          <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1.5">IXEN_TOKEN</div>
          <div className="bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg p-3 break-all">
            <code className="text-[#38bdf8] text-xs font-mono">{token}</code>
          </div>
        </div>

        {/* Copia token */}
        <button
          onClick={handleCopyToken}
          className={`w-full py-2.5 rounded-lg text-xs font-bold transition-all mb-3 border ${
            tokenCopied
              ? 'bg-green-900/40 border-green-700 text-green-400'
              : 'bg-[#1e3a5f]/50 border-[#1e3a5f] text-[#38bdf8] hover:bg-[#1e3a5f]'
          }`}
        >
          {tokenCopied ? '✓ Token copiato!' : 'Copia token'}
        </button>

        {/* URL Cloud — solo MT5 */}
        {type === 'PROP' && (
          <>
            <div className="mb-1">
              <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1.5">CLOUD_URL</div>
              <div className="bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg p-3">
                <code className="text-[#94a3b8] text-xs font-mono">{IXEN_CLOUD_URL}</code>
              </div>
            </div>
            <button
              onClick={handleCopyUrl}
              className={`w-full py-2.5 rounded-lg text-xs font-bold transition-all mb-3 border ${
                urlCopied
                  ? 'bg-green-900/40 border-green-700 text-green-400'
                  : 'bg-[#1e3a5f]/30 border-[#1e3a5f]/60 text-[#64748b] hover:bg-[#1e3a5f]/50 hover:text-[#94a3b8]'
              }`}
            >
              {urlCopied ? '✓ URL copiato!' : 'Copia URL'}
            </button>
          </>
        )}

        <div className="bg-[#0a0f1e] rounded-lg p-3 mb-4 border border-[#1e3a5f]/40">
          <p className="text-[10px] text-[#475569]">
            Incolla <code className="text-[#64748b]">IXEN_TOKEN</code> nell'EA{' '}
            {type === 'PROP' ? '(TradeAssist MT5)' : '(iXen Bridge MT4)'}.
            {type === 'PROP' && <> Incolla <code className="text-[#64748b]">CLOUD_URL</code> nel campo URL del server.</>}
            {' '}Il token identifica univocamente questo conto.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex-1 py-2.5 rounded-lg border border-orange-800 text-orange-400 text-xs font-bold hover:bg-orange-900/20 disabled:opacity-40 transition-all"
          >
            {resetting ? 'Reset...' : '↺ Rigenera token'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-[#1e3a5f] text-[#64748b] text-xs font-bold hover:bg-[#1e3a5f]/30 transition-all">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Card coppia ───────────────────────────────────────────────────────────────

function SlTpSlider({
  label, value, min, max, step, color, onChange,
}: {
  label: string; value: number; min: number; max: number; step: number
  color: 'orange' | 'green'; onChange: (v: number) => void
}) {
  const pct = (value * 100).toFixed(2) + '%'
  const maxPct = (max * 100).toFixed(1) + '%'
  const textCls = color === 'orange' ? 'text-orange-400' : 'text-green-400'
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider">{label}</span>
        <span className={`text-xs font-mono font-black ${textCls}`}>{pct}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ accentColor: color === 'orange' ? '#f97316' : '#4ade80' }}
        className="w-full h-1.5 rounded-full cursor-pointer"
      />
      <div className="flex justify-between text-[9px] text-[#334155] mt-0.5">
        <span>{(min * 100).toFixed(1)}%</span>
        <span>max {maxPct}</span>
      </div>
    </div>
  )
}

function DiagRow({ label, value, ok, note }: {
  label: string; value: string; ok: boolean; note: string
}) {
  const dotColor = ok ? '#1d9e75' : '#e24b4a'
  const valColor = ok ? '#4ec994' : '#f07070'
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dotColor }} />
      <span className="text-[#64748b] flex-shrink-0 w-16">{label}</span>
      <span className="font-mono font-bold flex-1 truncate" style={{ color: valColor }}>{value}</span>
      <span className="text-[#475569] text-[9px] flex-shrink-0">{note}</span>
    </div>
  )
}

function regimeColor(regime: string | null | undefined): string {
  switch (regime) {
    case 'TREND_BULL': return '#4ec994'
    case 'TREND_BEAR': return '#f07070'
    case 'RANGE':      return '#e9a84c'
    default:           return '#6a7490'
  }
}

function MarketContextPanel({ ctx }: { ctx: MarketContextResponse | null }) {
  const [open, setOpen] = useState(false)
  if (!ctx) return null

  const headerColor = ctx.available ? regimeColor(ctx.regime) : '#6a7490'
  const headerLabel = ctx.available ? (ctx.regime ?? 'UNDEFINED') : 'NON DISPONIBILE'

  // M15 è il timeframe principale (default). H1 resta filtro/diagnostica.
  const useM15  = ctx.use_m15_regime ?? true
  const adxMain = useM15 ? ctx.adx_m15 : ctx.adx_h1
  const adxH1Threshold = ctx.adx_h1_filter_threshold ?? 16
  const principalLabel = useM15 ? 'M15' : 'H1'

  // Conteggi e min coerenti col timeframe principale
  const principalCount = useM15 ? ctx.m15_candles_count : ctx.h1_candles_count
  const principalMin   = useM15 ? ctx.m15_candles_min   : ctx.h1_candles_min

  return (
    <div className="rounded-lg border border-[#1e3a5f]/60 bg-[#0a0f1e] mb-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[#0c1426] transition-colors rounded-lg"
      >
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: headerColor }} />
        <span className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-wider">Yomi — MarketContext</span>
        <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: headerColor }}>{headerLabel}</span>
        <span className="text-[9px] text-[#475569] ml-auto">
          {ctx.available
            ? `bias ${ctx.bias ?? '—'} · ADX ${principalLabel} ${adxMain?.toFixed(1) ?? '—'}`
            : `${principalCount}/${principalMin} candele ${principalLabel}`}
        </span>
        <span className="text-[#475569] text-[10px] flex-shrink-0">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-[#1e3a5f]/40">
          {!ctx.available && (
            <div className="text-[10px] text-[#94a3b8] py-2">
              {principalCount < principalMin
                ? `Raccolta dati in corso — ${principalCount}/${principalMin} candele ${principalLabel} disponibili`
                : 'MarketContext non ancora calcolato — riprova tra 30 secondi'}
            </div>
          )}
          {ctx.available && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 pt-1">
              <DiagRow
                label={`ADX ${principalLabel}`}
                value={adxMain?.toFixed(1) ?? '—'}
                ok={(adxMain ?? 0) >= ctx.adx_threshold}
                note={`min ${ctx.adx_threshold} (principale)`}
              />
              <DiagRow
                label="ADX H1"
                value={ctx.adx_h1?.toFixed(1) ?? '—'}
                ok={(ctx.adx_h1 ?? 0) >= adxH1Threshold}
                note={`min ${adxH1Threshold} (filtro)`}
              />
              <DiagRow
                label="Regime"
                value={ctx.regime ?? '—'}
                ok={ctx.regime === 'TREND_BULL' || ctx.regime === 'TREND_BEAR' || ctx.regime === 'RANGE'}
                note="≠ UNDEFINED"
              />
              <DiagRow
                label="Bias"
                value={ctx.bias ?? '—'}
                ok={ctx.bias === 'BUY' || ctx.bias === 'SELL'}
                note="BUY o SELL"
              />
              <DiagRow
                label={`EMA20 ${principalLabel}`}
                value={(useM15 ? ctx.ema_m15 : ctx.ema_h1)?.toFixed(2) ?? '—'}
                ok={(useM15 ? ctx.ema_m15 : ctx.ema_h1) != null}
                note={`prezzo ${(useM15 ? ctx.price_vs_ema_m15 : ctx.price_vs_ema) === 'ABOVE' ? 'sopra' : 'sotto'}`}
              />
              <DiagRow
                label={`Candele ${principalLabel}`}
                value={`${principalCount} / ${principalMin}`}
                ok={principalCount >= principalMin}
                note="min per analisi"
              />
            </div>
          )}
          <div className="text-[9px] text-[#475569] mt-2 text-right">
            aggiornato {parseUtcDate(ctx.evaluated_at).toLocaleTimeString('it-IT')}
          </div>
        </div>
      )}
    </div>
  )
}


// Mappa score 0..1 → livello visuale a 4 categorie.
// Soglie deterministiche su float originale (no rounding "drift"):
//   <0.56  → IN ATTESA (rosso)
//   <0.71  → MEDIO     (ambra)
//   <0.86  → FORTE     (sky)
//   ≥0.86  → OTTIMO    (blu)
function yomiStrength(score: number | undefined | null): {
  label: string; color: string; textColor: string; borderColor: string; dotIndex: number; pct: number
} {
  const s = Math.max(0, Math.min(1, score ?? 0))
  const pct = Math.floor(s * 100)
  if (s >= 0.86) return { label: 'OTTIMO',    color: 'bg-blue-500',  textColor: 'text-blue-300', borderColor: 'border-blue-600',   dotIndex: 3, pct }
  if (s >= 0.71) return { label: 'FORTE',     color: 'bg-sky-400',   textColor: 'text-sky-300',  borderColor: 'border-sky-500',    dotIndex: 2, pct }
  if (s >= 0.56) return { label: 'MEDIO',     color: 'bg-amber-500', textColor: 'text-amber-400', borderColor: 'border-amber-700', dotIndex: 1, pct }
  return                 { label: 'IN ATTESA', color: 'bg-rose-500',  textColor: 'text-rose-400', borderColor: 'border-rose-700/60', dotIndex: 0, pct }
}

const YOMI_REASON_PREFIXES: Record<string, string> = {
  m5_trigger_failed_high: 'Trigger M5 non confermato',
  m3_not_ready:           'Pre-trigger M3 non pronto',
  m1_no_entry_low:        'Entry M1 non confermato',
  prime_pulling_back:     'PRIME in pullback',
}

const YOMI_REASON_MAP: Record<string, string> = {
  operation_already_open:           'Operazione già aperta',
  no_signal:                        'Nessun segnale al momento',
  not_authorized:                   'Condizioni non soddisfatte',
  authorized:                       'Segnale autorizzato',
  regime_undefined_or_no_bias:      'Mercato indeciso — nessun trend riconoscibile',
  h1_filter_adx_too_low:            'Mercato laterale — ADX orario troppo basso',
  m15_insufficient_candles:         'Raccolta dati M15 in corso',
  m15_undefined_no_bias:            'ADX M15 presente ma direzione non confermata',
  m15_range_too_narrow:             'Range M15 troppo stretto',
  m15_adx_limbo:                    'ADX M15 in zona grigia',
  m15_compute_error:                'Errore tecnico nel calcolo M15',
  m15_trend_bull:                   'Trend rialzista M15 confermato',
  m15_trend_bear:                   'Trend ribassista M15 confermato',
  m15_range:                        'M15 in fase di range',
  h1_insufficient_candles:          'Raccolta dati H1 in corso',
  h1_undefined_no_bias:             'ADX H1 presente ma direzione non confermata',
  h1_range_too_narrow:              'Range H1 troppo stretto',
  h1_compute_error:                 'Errore tecnico nel calcolo H1',
  h1_trend_bull:                    'Trend rialzista H1 confermato',
  h1_trend_bear:                    'Trend ribassista H1 confermato',
  h1_range:                         'H1 in fase di range',
  expired:                          'Segnale scaduto',
  signal_expired:                   'Segnale scaduto',
  price_stale:                      'Prezzo EA non aggiornato',
  stale_price:                      'Prezzo EA non aggiornato (>15s)',
  ea_offline:                       'EA offline',
  price_invalid:                    'Prezzo fuori range',
  price_invalidated:                'Setup bruciato — prezzo ha superato la soglia',
  confidence_too_low:               'Confidenza insufficiente',
  confidence_below_cycle_threshold: 'Confidenza insufficiente per il ciclo corrente',
  missing_generated_at:             'Dato mancante nel segnale',
  missing_reference_price:          'Prezzo di riferimento mancante',
  invalid_signal_class:             'Classe segnale non riconosciuta',
  invalid_direction:                'Direzione segnale non valida',
  budget_exhausted:                 'Budget giornaliero esaurito',
  context_unavailable:              'Contesto di mercato non ancora pronto',
  phase_payout_target_reached:      '🎯 Target payout raggiunto — chiudi posizioni e richiedi payout',
  sl_capped_by_dd_buffer:           'Buffer DD esaurito — SL ridotto sotto soglia minima',
  prime_disabled:                   'Modalità PRIME disattivata',
  shadow_mode_active:               'Yomi in modalità osservazione (segnale non eseguibile)',
  no_pullback_detected:             'manca il pullback nel trend',
  momentum_not_exhausted:           'momentum del trend non si è esaurito',
  no_structural_signal_B_or_C:      'manca un pattern strutturale (HH/LL/engulfing/CHoCH)',
  veto_strong_bearish_momentum:     'veto: momentum ribassista ancora forte',
  veto_strong_bullish_momentum:     'veto: momentum rialzista ancora forte',
  range_veto_neutral_momentum:      'range: momentum neutro, nessuna inversione',
  range_momentum_not_exhausting:    'range: momentum non in esaurimento',
  range_no_inversion_trigger:       'range: nessun trigger di inversione',
  m5_pulling_back_soft_warning:     'M5 in leggero pullback',
  all_trending_aligned:             'tutti i timeframe ancora in trend pieno',
  m1_trending_m3_consolidating:     'M1 in trend ma M3 sta consolidando',
  consolidating_no_pullback:        'consolidamento senza pullback',
}

function humanizeYomiReason(reason: string | null | undefined): string {
  if (!reason || !reason.trim()) return 'In attesa di segnale…'
  const r = reason.trim()
  if (YOMI_REASON_MAP[r]) return YOMI_REASON_MAP[r]
  const colonIdx = r.indexOf(':')
  if (colonIdx > 0) {
    const prefix = r.slice(0, colonIdx).trim()
    const suffix = r.slice(colonIdx + 1).trim()
    const prefixIt = YOMI_REASON_PREFIXES[prefix]
    if (prefixIt) {
      const suffixIt = YOMI_REASON_MAP[suffix] ?? suffix.replace(/_/g, ' ')
      return `${prefixIt} — ${suffixIt}`
    }
  }
  return 'In attesa delle condizioni di ingresso'
}

function YomiDetailsPanel({ signal }: { signal: YomiSignalResponse }) {
  const diag = signal.diagnostics
  if (!diag) return null
  const useM15 = diag.use_m15_regime ?? true
  const adxH1Min = diag.adx_h1_filter_threshold ?? 16
  const principalLabel = useM15 ? 'M15' : 'H1'
  const principalCount = useM15 ? (diag.m15_candles_count ?? 0) : (signal.h1_candles_count ?? diag.h1_candles_count ?? 0)
  const principalMin   = useM15 ? (diag.m15_candles_min   ?? 26) : (signal.h1_candles_min ?? diag.h1_candles_min ?? 26)
  return (
    <details className="bg-[#0a0f1e] rounded-lg border border-[#1e3a5f]/40">
      <summary className="cursor-pointer px-3 py-2 text-[11px] font-bold text-[#94a3b8] uppercase tracking-wider list-none flex items-center justify-between hover:text-white transition-colors">
        <span>▾ Dettagli tecnici</span>
        <span className="text-[#475569]">+</span>
      </summary>
      <div className="px-3 pb-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
        <DiagRow
          label={`ADX ${principalLabel}`}
          value={(useM15 ? diag.adx_m15 : diag.adx_h1) != null
            ? (useM15 ? diag.adx_m15! : diag.adx_h1!).toFixed(1) : '—'}
          ok={(useM15 ? (diag.adx_m15 ?? 0) : (diag.adx_h1 ?? 0)) >= diag.adx_threshold}
          note={`min ${diag.adx_threshold}`}
        />
        <DiagRow
          label="ADX H1"
          value={diag.adx_h1 != null ? diag.adx_h1.toFixed(1) : '—'}
          ok={(diag.adx_h1 ?? 0) >= adxH1Min}
          note={`min ${adxH1Min}`}
        />
        <DiagRow
          label="Regime"
          value={diag.regime ?? '—'}
          ok={diag.regime === 'TREND_BULL' || diag.regime === 'TREND_BEAR' || diag.regime === 'RANGE'}
          note="≠ UNDEFINED"
        />
        <DiagRow
          label="Bias"
          value={diag.bias ?? '—'}
          ok={diag.bias === 'BUY' || diag.bias === 'SELL'}
          note="BUY o SELL"
        />
        <div className="col-span-2">
          <DiagRow
            label={`Candele ${principalLabel}`}
            value={`${principalCount} / ${principalMin}`}
            ok={principalCount >= principalMin}
            note={`min ${principalMin}`}
          />
        </div>
      </div>
    </details>
  )
}

// ─── User-facing badge classes (Tailwind static enumeration) ──────────────
const CHIP_CLASSES: Record<string, string> = {
  emerald: 'bg-emerald-900/50 border-emerald-700 text-emerald-200',
  lime:    'bg-lime-900/50 border-lime-700 text-lime-200',
  sky:     'bg-sky-900/50 border-sky-700 text-sky-200',
  blue:    'bg-blue-900/50 border-blue-700 text-blue-200',
  yellow:  'bg-yellow-900/50 border-yellow-700 text-yellow-200',
  amber:   'bg-amber-900/50 border-amber-700 text-amber-200',
  orange:  'bg-orange-900/50 border-orange-700 text-orange-200',
  rose:    'bg-rose-900/50 border-rose-700 text-rose-200',
  slate:   'bg-slate-800/60 border-slate-600 text-slate-300',
}

const RECO_BOX_CLASSES: Record<string, string> = {
  emerald: 'bg-gradient-to-r from-emerald-900/60 to-emerald-800/30 border-emerald-600 text-emerald-100',
  lime:    'bg-gradient-to-r from-lime-900/60 to-lime-800/30 border-lime-600 text-lime-100',
  yellow:  'bg-gradient-to-r from-yellow-900/60 to-yellow-800/30 border-yellow-600 text-yellow-100',
  amber:   'bg-gradient-to-r from-amber-900/60 to-amber-800/30 border-amber-600 text-amber-100',
  rose:    'bg-gradient-to-r from-rose-900/60 to-rose-800/30 border-rose-600 text-rose-100',
  slate:   'bg-slate-800/40 border-slate-600 text-slate-300',
}

function chipCls(color?: string): string {
  return CHIP_CLASSES[color || 'slate'] || CHIP_CLASSES.slate
}
function recoBoxCls(color?: string): string {
  return RECO_BOX_CLASSES[color || 'slate'] || RECO_BOX_CLASSES.slate
}

function UserFacingBadges({ uf }: { uf: NonNullable<YomiSignalResponse['user_facing']> }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-[#1e3a5f]/60">
      <span
        className={`text-[10px] font-black px-2 py-1 rounded-md border ${chipCls(uf.signal_color)} uppercase tracking-wider`}
        title="Qualità del segnale Yomi"
      >
        {uf.signal_label}
      </span>
      {uf.hour && (
        <span
          className={`text-[10px] font-bold px-2 py-1 rounded-md border ${chipCls(uf.hour.color)}`}
          title="Ora storicamente favorevole su XAU"
        >
          {uf.hour.icon} {String(uf.hour.hour_utc).padStart(2, '0')}:00 UTC · {uf.hour.label}
        </span>
      )}
      {uf.phase.adx_phase && (
        <span
          className={`text-[10px] font-bold px-2 py-1 rounded-md border ${chipCls(uf.phase.color)}`}
          title="Fase del trend ADX"
        >
          {uf.phase.icon} {uf.phase.label}
        </span>
      )}
    </div>
  )
}

function RecommendationBox({ uf }: { uf: NonNullable<YomiSignalResponse['user_facing']> }) {
  const r = uf.recommendation
  if (r.level === 'NONE') return null
  return (
    <div
      className={`mt-2 rounded-lg border px-3 py-2 ${recoBoxCls(r.color)}`}
      title={r.details}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-base leading-none">{r.icon}</span>
          <span className="text-[12px] font-black uppercase tracking-wide">{r.label}</span>
        </div>
        {r.score != null && (
          <span className="text-[10px] opacity-70 font-bold tabular-nums">score {r.score >= 0 ? '+' : ''}{r.score}</span>
        )}
      </div>
    </div>
  )
}

// ── SessionQualityBadge — Yomi v4.1 (30/04/2026) ─────────────────────────────
// Badge informativo della fascia oraria storica (label/win-rate/avg-pts).
// Mai blocca i segnali: l'utente decide se eseguire.
// Visualizzato sopra le metriche SL/TP del banner Yomi.
function SessionQualityBadge({ sq }: { sq: SessionQualityData }) {
  const colorMap: Record<SessionQualityData['color'], { bg: string; border: string; text: string }> = {
    green:  { bg: 'bg-emerald-900/40', border: 'border-emerald-600/60', text: 'text-emerald-300' },
    blue:   { bg: 'bg-sky-900/40',     border: 'border-sky-600/60',     text: 'text-sky-300'     },
    yellow: { bg: 'bg-amber-900/40',   border: 'border-amber-600/60',   text: 'text-amber-300'   },
    red:    { bg: 'bg-rose-900/40',    border: 'border-rose-600/60',    text: 'text-rose-300'    },
  }
  const c   = colorMap[sq.color] ?? colorMap.blue
  const wr  = Number.isFinite(sq.win_rate_pct) ? sq.win_rate_pct.toFixed(0) : '—'
  const avg = Number.isFinite(sq.avg_pts) ? `${sq.avg_pts >= 0 ? '+' : ''}${sq.avg_pts.toFixed(1)}` : '—'
  return (
    <div className={`mt-3 px-2.5 py-1.5 rounded-lg border ${c.bg} ${c.border} flex items-center gap-2`}>
      <span className="text-[14px] leading-none">{sq.emoji ?? '⚪'}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] font-black uppercase tracking-wider ${c.text}`}>{sq.label}</span>
          {sq.session_name && (
            <span className="text-[9px] text-[#94a3b8] font-bold uppercase">· {sq.session_name}</span>
          )}
          <span className="ml-auto text-[10px] text-[#cbd5e1] font-bold tabular-nums">
            WR <b className="text-white">{wr}%</b> · avg <b className="text-white">{avg}</b>
          </span>
        </div>
        {sq.message && (
          <div className="text-[10px] text-[#94a3b8] mt-0.5 leading-snug truncate" title={sq.message}>
            {sq.message}
          </div>
        )}
      </div>
    </div>
  )
}

// Signal Persistence — mostra l'ultimo ALLOW recente quando il segnale "live"
// è inattivo. Solo informativo: pulsante ESEGUI assente per design (l'utente
// deve attendere un nuovo ALLOW vivo o eseguire manualmente dal Calcolatore).
function PersistentSignalCard({ ls }: { ls: NonNullable<YomiLastSignalResponse['signal']> }) {
  const dir = ls.prop_direction || ls.direction
  if (!dir) return null
  const isBuy = dir === 'BUY'
  const ageMin = Math.floor((ls.age_seconds || 0) / 60)
  const ageSec = (ls.age_seconds || 0) % 60
  const ttlMin = Math.floor((ls.ttl_seconds || 0) / 60)
  const ttlSec = (ls.ttl_seconds || 0) % 60
  const ttlPct = (() => {
    const total = (ls.age_seconds || 0) + (ls.ttl_seconds || 0)
    if (total <= 0) return 0
    return Math.max(0, Math.min(100, ((ls.ttl_seconds || 0) / total) * 100))
  })()
  const dirCls = isBuy
    ? 'bg-sky-900/60 border-sky-700 text-sky-200'
    : 'bg-rose-900/60 border-rose-700 text-rose-200'
  const dirArrow = isBuy ? '▲' : '▼'
  return (
    <div className="rounded-xl border border-amber-700/40 bg-gradient-to-br from-[#1a1408] to-[#0f0a05] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black px-2 py-0.5 rounded bg-amber-900/60 border border-amber-700 text-amber-200 uppercase tracking-wider">
            ⏱ Ultimo segnale
          </span>
          <span className={`text-[11px] font-black px-2 py-0.5 rounded border ${dirCls}`}>
            {dirArrow} {dir}
          </span>
        </div>
        <div className="text-[10px] text-amber-300/80 font-bold" style={{fontVariantNumeric:'tabular-nums'}}>
          {ageMin}m {ageSec}s fa
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] text-[#cbd5e1] mb-2">
        <div>
          <span className="text-[#64748b] block text-[9px] uppercase font-bold tracking-wider">Prezzo rif.</span>
          <b className="text-white" style={{fontVariantNumeric:'tabular-nums'}}>
            {ls.reference_price != null ? ls.reference_price.toFixed(2) : '—'}
          </b>
        </div>
        <div>
          <span className="text-[#64748b] block text-[9px] uppercase font-bold tracking-wider">Scenario</span>
          <b className="text-white text-[10px]">{ls.scenario || '—'}</b>
        </div>
        <div>
          <span className="text-[#64748b] block text-[9px] uppercase font-bold tracking-wider">Score</span>
          <b className="text-white" style={{fontVariantNumeric:'tabular-nums'}}>
            {ls.score != null ? Math.round(ls.score * 100) : '—'}
          </b>
        </div>
      </div>
      <div className="relative h-1.5 bg-[#1e293b] rounded-full overflow-hidden">
        <div
          className="absolute h-1.5 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all"
          style={{ width: `${ttlPct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[9px] text-[#64748b] uppercase tracking-wider font-bold">Validità residua</span>
        <span className="text-[10px] text-amber-300 font-black" style={{fontVariantNumeric:'tabular-nums'}}>
          {ttlMin}m {ttlSec.toString().padStart(2,'0')}s
        </span>
      </div>
    </div>
  )
}

function YomiBanner({ signal, lastSignal, pairId, onUse, onDismiss }: {
  signal: YomiSignalResponse | null
  lastSignal?: YomiLastSignalResponse | null
  pairId: number
  onUse: (signal: YomiSignalResponse, pairId: number) => void
  onDismiss: (evaluatedAt: string | undefined) => void
}) {
  if (!signal) return null

  // ── STATO IN ATTESA (no segnale attivo) ──
  if (!signal.active) {
    const label = humanizeYomiReason(signal.reason)
    const hasValidLastSignal = !!(
      lastSignal?.active &&
      lastSignal.signal &&
      lastSignal.signal.ttl_seconds > 0 &&
      lastSignal.signal.prop_direction &&
      signal.reason !== 'operation_already_open'
    )

    if (hasValidLastSignal) {
      const ls = lastSignal!.signal!
      const lsDir = ls.prop_direction!
      const lsIsBuy = lsDir === 'BUY'
      const lsStrength = yomiStrength(ls.score)
      const lsDirBadgeCls = lsIsBuy
        ? 'bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] text-white shadow-[0_2px_8px_rgba(14,165,233,0.4)]'
        : 'bg-gradient-to-b from-rose-600 to-rose-800 text-white shadow-[0_2px_8px_rgba(244,63,94,0.4)]'
      const lsExecBtnCls = lsIsBuy
        ? 'bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] border border-[#38bdf8] text-white shadow-[0_2px_10px_rgba(14,165,233,0.4)] hover:from-[#0284c7] hover:to-[#075985]'
        : 'bg-gradient-to-b from-rose-600 to-rose-800 border border-rose-500 text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)] hover:from-rose-700 hover:to-rose-900'

      const ttlMin = Math.floor(ls.ttl_seconds / 60)
      const ttlSec = ls.ttl_seconds % 60
      const ttlPct = (() => {
        const total = (ls.age_seconds || 0) + ls.ttl_seconds
        if (total <= 0) return 0
        return Math.max(0, Math.min(100, (ls.ttl_seconds / total) * 100))
      })()

      return (
        <div className="space-y-2.5">
          <div className={`rounded-xl border ${lsStrength.borderColor} bg-gradient-to-br from-[#0c1d3a] to-[#0a1628] p-4 shadow-[0_0_20px_rgba(59,130,246,0.12)]`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`text-[13px] font-black px-2.5 py-1.5 rounded-lg ${lsDirBadgeCls}`}>
                  {lsIsBuy ? '▲' : '▼'} {lsDir}
                </span>
                <span className="text-[10px] text-amber-300/70 uppercase tracking-wider font-bold">Ultimo segnale</span>
              </div>
              <div className="text-right">
                <div className="text-[24px] font-black text-white leading-none" style={{fontVariantNumeric:'tabular-nums'}}>
                  {lsStrength.pct}<span className="text-[12px] text-[#64748b] font-bold"> /100</span>
                </div>
              </div>
            </div>

            <div className="relative h-2 bg-[#1e293b] rounded-full overflow-hidden">
              <div
                className="absolute h-2 bg-gradient-to-r from-rose-500 via-amber-400 to-blue-500 rounded-full"
                style={{ width: `${Math.max(8, lsStrength.pct)}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-1.5">
                {[0,1,2,3].map(i => {
                  const colors = ['bg-rose-500','bg-amber-500','bg-sky-400','bg-blue-500']
                  const glow   = ['shadow-[0_0_8px_#f43f5e]','shadow-[0_0_8px_#f59e0b]','shadow-[0_0_8px_#38bdf8]','shadow-[0_0_8px_#3b82f6]']
                  const isActive = i === lsStrength.dotIndex
                  return (
                    <span key={i} className={`w-2.5 h-2.5 rounded-full ${colors[i]} ${isActive ? glow[i] : 'opacity-25'}`} />
                  )
                })}
              </div>
              <span className={`text-[11px] font-black uppercase tracking-wider ${lsStrength.textColor}`}>{lsStrength.label}</span>
            </div>

            <div className="grid grid-cols-3 gap-2 text-[11px] text-[#cbd5e1] mt-3 pt-2 border-t border-[#1e3a5f]/60">
              <div>
                <span className="text-[#64748b] block text-[9px] uppercase font-bold tracking-wider">Prezzo rif.</span>
                <b className="text-white" style={{fontVariantNumeric:'tabular-nums'}}>
                  {ls.reference_price != null ? ls.reference_price.toFixed(2) : '—'}
                </b>
              </div>
              <div>
                <span className="text-[#64748b] block text-[9px] uppercase font-bold tracking-wider">Scenario</span>
                <b className="text-white text-[10px]">{ls.scenario || '—'}</b>
              </div>
              <div>
                <span className="text-[#64748b] block text-[9px] uppercase font-bold tracking-wider">Classe</span>
                <b className="text-white">{ls.signal_class || '—'}</b>
              </div>
            </div>

            <div className="mt-3 pt-2 border-t border-amber-700/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] text-amber-300/80 uppercase tracking-wider font-bold">Validit\u00e0 residua</span>
                <span className="text-[10px] text-amber-300 font-black" style={{fontVariantNumeric:'tabular-nums'}}>
                  {ttlMin}m {ttlSec.toString().padStart(2,'0')}s
                </span>
              </div>
              <div className="relative h-1.5 bg-[#1e293b] rounded-full overflow-hidden">
                <div
                  className="absolute h-1.5 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all"
                  style={{ width: `${ttlPct}%` }}
                />
              </div>
            </div>

            {signal.session_quality && <SessionQualityBadge sq={signal.session_quality} />}
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <button
              disabled
              title="Il segnale live non è più attivo — attendi il prossimo aggiornamento"
              className="py-2.5 rounded-lg font-black text-[12px] bg-[#1e293b] border border-[#334155] text-[#64748b] cursor-not-allowed opacity-60"
            >
              ⏸ Solo riferimento
            </button>
            <button
              onClick={() => onDismiss(ls.decision_at)}
              title="Nasconde questo segnale fino al prossimo aggiornamento"
              className="py-2.5 rounded-lg font-bold text-[12px] bg-[#0d1529] border border-[#475569] text-[#cbd5e1] hover:border-[#94a3b8] hover:text-white transition-all"
            >
              Ignora segnale
            </button>
          </div>

          <YomiDetailsPanel signal={signal} />
        </div>
      )
    }

    return (
      <div className="space-y-2.5">
        <div className="rounded-xl border border-rose-700/60 bg-gradient-to-br from-[#1a0a14] to-[#0f0710] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-black px-2.5 py-1 rounded-lg bg-rose-900/60 border border-rose-700 text-rose-200">⊙ NO TRADE</span>
              <span className="text-[10px] text-[#94a3b8] uppercase tracking-wider font-bold">Stato</span>
            </div>
            <div className="text-right">
              <div className="text-[22px] font-black text-rose-400/70 leading-none" style={{fontVariantNumeric:'tabular-nums'}}>
                — <span className="text-[12px] text-[#64748b] font-bold">/100</span>
              </div>
            </div>
          </div>
          <div className="relative h-2 bg-[#1e293b] rounded-full overflow-hidden">
            <div className="absolute h-2 bg-rose-900/50 rounded-full" style={{width:'8%'}} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-[0_0_8px_#f43f5e]" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 opacity-20" />
              <span className="w-2.5 h-2.5 rounded-full bg-sky-400 opacity-20" />
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 opacity-20" />
            </div>
            <span className="text-[11px] font-black text-rose-400 uppercase tracking-wider">In attesa</span>
          </div>
          <div className="text-[12px] text-[#cbd5e1] mt-3 pt-2 border-t border-rose-900/40 text-center">{label}</div>
          {signal.user_facing && <UserFacingBadges uf={signal.user_facing} />}
          {signal.session_quality && <SessionQualityBadge sq={signal.session_quality} />}
        </div>
        <YomiDetailsPanel signal={signal} />
      </div>
    )
  }

  // ── STATO SEGNALE ATTIVO ──
  const dir = signal.prop_direction!
  const isBuy = dir === 'BUY'
  const strength = yomiStrength(signal.score)
  const dirBadgeCls = isBuy
    ? 'bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] text-white shadow-[0_2px_8px_rgba(14,165,233,0.4)]'
    : 'bg-gradient-to-b from-rose-600 to-rose-800 text-white shadow-[0_2px_8px_rgba(244,63,94,0.4)]'
  const execBtnCls = isBuy
    ? 'bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] border border-[#38bdf8] text-white shadow-[0_2px_10px_rgba(14,165,233,0.4)] hover:from-[#0284c7] hover:to-[#075985]'
    : 'bg-gradient-to-b from-rose-600 to-rose-800 border border-rose-500 text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)] hover:from-rose-700 hover:to-rose-900'

  return (
    <div className="space-y-2.5">
      <div className={`rounded-xl border ${strength.borderColor} bg-gradient-to-br from-[#0c1d3a] to-[#0a1628] p-4 shadow-[0_0_20px_rgba(59,130,246,0.12)]`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`text-[13px] font-black px-2.5 py-1.5 rounded-lg ${dirBadgeCls}`}>
              {isBuy ? '▲' : '▼'} {dir}
            </span>
            <span className="text-[10px] text-[#cbd5e1] uppercase tracking-wider font-bold">Segnale</span>
          </div>
          <div className="text-right">
            <div className="text-[24px] font-black text-white leading-none" style={{fontVariantNumeric:'tabular-nums'}}>
              {strength.pct}<span className="text-[12px] text-[#64748b] font-bold"> /100</span>
            </div>
          </div>
        </div>

        {/* Barra forza */}
        <div className="relative h-2 bg-[#1e293b] rounded-full overflow-hidden">
          <div
            className="absolute h-2 bg-gradient-to-r from-rose-500 via-amber-400 to-blue-500 rounded-full"
            style={{ width: `${Math.max(8, strength.pct)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1.5">
            {[0,1,2,3].map(i => {
              const colors = ['bg-rose-500','bg-amber-500','bg-sky-400','bg-blue-500']
              const glow   = ['shadow-[0_0_8px_#f43f5e]','shadow-[0_0_8px_#f59e0b]','shadow-[0_0_8px_#38bdf8]','shadow-[0_0_8px_#3b82f6]']
              const isActive = i === strength.dotIndex
              return (
                <span key={i} className={`w-2.5 h-2.5 rounded-full ${colors[i]} ${isActive ? glow[i] : 'opacity-25'}`} />
              )
            })}
          </div>
          <span className={`text-[11px] font-black uppercase tracking-wider ${strength.textColor}`}>{strength.label}</span>
        </div>

        {/* Badge user-facing (PREMIUM/STANDARD + Ora + Fase) */}
        {signal.user_facing && <UserFacingBadges uf={signal.user_facing} />}

        {/* Yomi v4.1: badge SessionQuality (fascia oraria storica) */}
        {signal.session_quality && <SessionQualityBadge sq={signal.session_quality} />}

        {/* Box raccomandazione (verde/giallo/rosso) */}
        {signal.user_facing && <RecommendationBox uf={signal.user_facing} />}

        {/* Riassunto SL/TP/Spread */}
        <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-[#1e3a5f]/60 text-[12px] text-[#cbd5e1]">
          <div>
            <span className="text-[#64748b] block text-[10px] uppercase font-bold tracking-wider">SL</span>
            <b className="text-white" style={{fontVariantNumeric:'tabular-nums'}}>{signal.sl_percent != null ? (signal.sl_percent*100).toFixed(2)+'%' : '—'}</b>
          </div>
          <div>
            <span className="text-[#64748b] block text-[10px] uppercase font-bold tracking-wider">TP</span>
            <b className="text-white" style={{fontVariantNumeric:'tabular-nums'}}>{signal.tp_percent != null ? (signal.tp_percent*100).toFixed(2)+'%' : '—'}</b>
          </div>
          <div>
            <span className="text-[#64748b] block text-[10px] uppercase font-bold tracking-wider">Classe</span>
            <b className="text-white">{signal.signal_class ?? '—'}</b>
          </div>
        </div>
      </div>

      {/* Pulsanti Esegui / Ignora */}
      <div className="grid grid-cols-2 gap-2.5">
        <button
          onClick={() => onUse(signal, pairId)}
          className={`py-2.5 rounded-lg font-black text-[12px] transition-all ${execBtnCls}`}
        >
          {isBuy ? '▲' : '▼'} Esegui da Yomi
        </button>
        <button
          onClick={() => onDismiss(signal.evaluated_at)}
          title="Nasconde questo segnale fino al prossimo aggiornamento"
          className="py-2.5 rounded-lg font-bold text-[12px] bg-[#0d1529] border border-[#475569] text-[#cbd5e1] hover:border-[#94a3b8] hover:text-white transition-all"
        >
          Ignora segnale
        </button>
      </div>

      <YomiDetailsPanel signal={signal} />
    </div>
  )
}


// ─── Parsing timestamp backend (UTC "naive", senza suffisso Z) ───────────────
// Il backend serializza datetime UTC senza indicatore di fuso: il browser li
// interpreterebbe come ora locale. Aggiungiamo la Z se manca, così ogni
// formattazione successiva (toLocaleTimeString ecc.) mostra l'ora locale reale.
export function parseUtcDate(iso: string): Date {
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso)
  return new Date(hasTz ? iso : iso + 'Z')
}

// ─── Tempo trascorso in italiano (da ISO UTC) ───────────────────────────────
function timeAgoIt(iso?: string | null): string {
  if (!iso) return ''
  const t = parseUtcDate(iso).getTime()
  if (Number.isNaN(t)) return ''
  const diffMs = Date.now() - t
  const m = Math.floor(diffMs / 60000)
  if (m < 1) return 'adesso'
  if (m < 60) return `${m} min fa`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h fa`
  return `${Math.floor(h / 24)}g fa`
}

// ─── Riga compatta motore (Zanshin) — Yomi / Hyoshi / Sempai, pari peso ─────
// active=true → bordo sinistro acceso (blu BUY / rosso SELL) + etichetta entrata
// a riquadro pieno; barra score multicolore per motore (barGradient). idle →
// grigia e silenziosa. scorePct=null → nessuna barra (Sempai non ha score).
function EngineRow({
  iconBg, iconText, glyph, name, role, badge,
  active, direction, scorePct, subRight, tagRight, context, barGradient,
}: {
  iconBg: string
  iconText: string
  glyph: string
  name: string
  role: string
  badge?: string
  active: boolean
  direction: 'BUY' | 'SELL' | null
  scorePct: number | null
  subRight?: string | null
  tagRight?: string | null
  context: string
  barGradient?: string
}) {
  const isBuy = direction === 'BUY'
  // Bordo laterale: blu (#0ea5e9, come il pulsante BUY) per BUY, rosso per SELL.
  const accent = !active
    ? 'border-l-[#1e293b]'
    : isBuy ? 'border-l-[#0ea5e9]' : 'border-l-rose-500'
  // Barra score: multicolore per motore (passata da chi chiama); idle grigia.
  const barFill = !active
    ? 'bg-[#1e293b]'
    : (barGradient ?? 'bg-gradient-to-r from-sky-500 to-sky-300')
  const barWidth = scorePct == null ? 0 : active ? Math.max(6, scorePct) : 4
  // Etichetta entrata a mercato: riquadro pieno, stesso look dei pulsanti BUY/SELL.
  const dirBadge = isBuy
    ? 'bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] text-white shadow-[0_2px_8px_rgba(14,165,233,0.45)]'
    : 'bg-gradient-to-b from-rose-600 to-rose-800 text-white shadow-[0_2px_8px_rgba(244,63,94,0.45)]'

  return (
    <div className={`rounded-lg border border-[#1e293b] border-l-2 ${accent} bg-[#0a0f1e]/80 px-3 py-2.5`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`shrink-0 w-7 h-7 rounded-md grid place-items-center text-[14px] ${iconBg} ${iconText}`}>
            {glyph}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-bold text-white truncate">{name}</span>
              {badge && (
                <span className="text-[8px] font-bold uppercase tracking-wider text-[#64748b] border border-[#334155] rounded px-1 py-0.5">
                  {badge}
                </span>
              )}
            </div>
            <div className="text-[10px] text-[#64748b] truncate">{role}</div>
          </div>
        </div>
        <div className="text-right shrink-0">
          {direction ? (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-black uppercase tracking-wide leading-none ${active ? dirBadge : 'bg-[#1e293b] text-[#475569]'}`}>
              <span className="text-[10px] leading-none">{isBuy ? '▲' : '▼'}</span>{direction}
            </span>
          ) : (
            <div className="text-[13px] font-black leading-none text-[#475569]">—</div>
          )}
          {(subRight || tagRight) && (
            <div className="text-[10px] text-[#64748b] mt-1.5" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {tagRight && <span className="text-amber-400/80 font-bold">{tagRight} · </span>}{subRight}
            </div>
          )}
        </div>
      </div>
      {scorePct != null && (
        <div className="relative h-1.5 bg-[#111827] rounded-full overflow-hidden mt-2">
          <div className={`absolute h-1.5 rounded-full ${barFill}`} style={{ width: `${barWidth}%` }} />
        </div>
      )}
      <div className="text-[10px] text-[#94a3b8] mt-1.5 truncate">{context}</div>
    </div>
  )
}

// ─── Dettaglio Sempai — lista alert esterni recenti (admin, solo notifica) ──
function SempaiDetail({ alerts }: { alerts: SempaiAlertItem[] }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="rounded-xl border border-amber-900/40 bg-gradient-to-br from-[#1a140a] to-[#0f0c07] px-3 py-3">
        <div className="text-[11px] text-[#94a3b8] text-center">
          Nessun alert esterno ricevuto di recente.
        </div>
      </div>
    )
  }
  return (
    <div className="rounded-xl border border-amber-900/40 bg-gradient-to-br from-[#1a140a] to-[#0f0c07] p-3 space-y-1.5">
      {alerts.map(a => {
        // La card mostra la direzione DA CLICCARE sul prop (invertita rispetto
        // all'alert SniperPro): alert BUY → prop SELL, alert SELL → prop BUY.
        const propDir = a.direction === 'BUY' ? 'SELL' : 'BUY'
        const isBuy = propDir === 'BUY'
        const statusColor =
          a.status === 'EXECUTED' ? 'text-emerald-300'
          : a.status === 'EXPIRED' ? 'text-[#64748b]'
          : 'text-amber-300'
        return (
          <div key={a.id} className="flex items-center justify-between gap-2 text-[11px] border-b border-amber-900/20 last:border-0 pb-1.5 last:pb-0">
            <span className={`font-black ${isBuy ? 'text-emerald-400' : 'text-rose-400'}`}>
              {isBuy ? '▲' : '▼'} {propDir}
              <span className="text-[#64748b] font-normal ml-1.5">(alert: {a.direction})</span>
            </span>
            <span className="text-[#94a3b8]">{timeAgoIt(a.received_at)}</span>
            <span className={`font-bold uppercase tracking-wider text-[9px] ${statusColor}`}>{a.status}</span>
          </div>
        )
      })}
      <div className="text-[9px] text-amber-400/50 text-center italic pt-1">
        Fonte segnali esterna · solo notifica, nessuna esecuzione automatica.
      </div>
    </div>
  )
}


function YomiConfirmModal({
  data, onConfirm, onCancel,
}: {
  data: YomiConfirmData
  onConfirm: () => void
  onCancel: () => void
}) {
  const COUNTDOWN_SECONDS = 30
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS)

  // Countdown 30s: parte all'apertura, scaduto → chiude da solo via onCancel
  useEffect(() => {
    setSecondsLeft(COUNTDOWN_SECONDS)
    const t = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(t)
          onCancel()
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.generatedAt, data.pairId])

  const isBuy = data.direction === 'BUY'
  const dirCls = isBuy ? 'text-sky-400' : 'text-rose-400'
  const dirIcon = isBuy ? '▲' : '▼'
  const ringPct = (secondsLeft / COUNTDOWN_SECONDS) * 100
  const urgent = secondsLeft <= 10

  // Bordo button colorato per direzione
  const execBtnCls = isBuy
    ? 'bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] border-[#38bdf8] hover:from-[#0284c7] hover:to-[#075985] shadow-[0_2px_12px_rgba(14,165,233,0.5)]'
    : 'bg-gradient-to-b from-rose-600 to-rose-800 border-rose-500 hover:from-rose-700 hover:to-rose-900 shadow-[0_2px_12px_rgba(244,63,94,0.5)]'

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a1628] border border-[#1e3a5f] rounded-2xl p-6 max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-bold text-[#64748b] uppercase tracking-wider">Conferma esecuzione Yomi</div>
            <div className="text-white font-black text-lg leading-tight mt-0.5">{data.pairName}</div>
          </div>
          <div className={`text-[28px] font-black ${dirCls} flex items-baseline gap-1`} style={{fontVariantNumeric:'tabular-nums'}}>
            {dirIcon}<span>{data.direction}</span>
          </div>
        </div>

        {/* Entry + Lots */}
        <div className="grid grid-cols-2 gap-2.5 mb-3">
          <div className="bg-[#0d1529] border border-[#1e3a5f]/60 rounded-lg p-3">
            <div className="text-[10px] text-[#64748b] uppercase font-bold tracking-wider mb-0.5">Entry PROP</div>
            <div className="text-white font-black text-base" style={{fontVariantNumeric:'tabular-nums'}}>
              {data.entryPrice != null ? `$${data.entryPrice.toFixed(2)}` : 'mercato'}
            </div>
          </div>
          <div className="bg-[#0d1529] border border-[#1e3a5f]/60 rounded-lg p-3">
            <div className="text-[10px] text-[#64748b] uppercase font-bold tracking-wider mb-0.5">Lotti P / B</div>
            <div className="text-white font-black text-base" style={{fontVariantNumeric:'tabular-nums'}}>
              {data.lotsProp != null ? data.lotsProp.toFixed(2) : '—'}
              <span className="text-[#64748b]"> / </span>
              {data.lotsBroker != null ? data.lotsBroker.toFixed(2) : '—'}
            </div>
          </div>
        </div>

        {/* SL / TP in $ di movimento prezzo */}
        <div className="grid grid-cols-2 gap-2.5 mb-3">
          <div className="bg-rose-950/30 border border-rose-900/50 rounded-lg p-3">
            <div className="text-[10px] text-rose-400/80 uppercase font-bold tracking-wider mb-0.5">Stop Loss</div>
            <div className="text-rose-300 font-black text-base" style={{fontVariantNumeric:'tabular-nums'}}>
              {data.slDistanceUsd != null ? `−$${data.slDistanceUsd.toFixed(2)}` : '—'}
            </div>
            <div className="text-[10px] text-rose-400/60 mt-0.5" style={{fontVariantNumeric:'tabular-nums'}}>
              {(data.slPercent*100).toFixed(2)}% · prezzo {data.slPropPrice != null ? `$${data.slPropPrice.toFixed(2)}` : '—'}
            </div>
          </div>
          <div className="bg-emerald-950/30 border border-emerald-900/50 rounded-lg p-3">
            <div className="text-[10px] text-emerald-400/80 uppercase font-bold tracking-wider mb-0.5">Take Profit</div>
            <div className="text-emerald-300 font-black text-base" style={{fontVariantNumeric:'tabular-nums'}}>
              {data.tpDistanceUsd != null ? `+$${data.tpDistanceUsd.toFixed(2)}` : '—'}
            </div>
            <div className="text-[10px] text-emerald-400/60 mt-0.5" style={{fontVariantNumeric:'tabular-nums'}}>
              {(data.tpPercent*100).toFixed(2)}% · prezzo {data.tpPropPrice != null ? `$${data.tpPropPrice.toFixed(2)}` : '—'}
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between text-[11px] text-[#64748b] mb-4 px-1">
          <span>Classe <b className="text-[#94a3b8]">{data.signalClass ?? '—'}</b></span>
          <span>Score <b className="text-[#94a3b8]" style={{fontVariantNumeric:'tabular-nums'}}>{data.score != null ? data.score.toFixed(2) : '—'}</b></span>
          {data.isFprPair && (
            <span>Margine <b className="text-[#94a3b8] capitalize">{data.fprMarginMode}</b></span>
          )}
        </div>

        {/* Pulsanti — countdown integrato nel bottone ESEGUI */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-lg border border-[#1e3a5f] text-[#94a3b8] text-sm font-bold hover:bg-[#1e3a5f]/30 transition-all"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-3 rounded-lg text-white text-sm font-black border transition-all relative overflow-hidden ${execBtnCls}`}
          >
            {/* Barra countdown sotto il testo */}
            <span
              className={`absolute bottom-0 left-0 h-1 transition-[width] duration-1000 linear ${urgent ? 'bg-yellow-300' : 'bg-white/40'}`}
              style={{ width: `${ringPct}%` }}
            />
            <span className="relative">ESEGUI · {secondsLeft}s</span>
          </button>
        </div>

        <div className="text-[10px] text-[#475569] text-center mt-3">
          Il segnale scade automaticamente tra {secondsLeft} secondi
        </div>
      </div>
    </div>
  )
}


function PairCard({
  pair, centralized, pairsCount,
  submitting, sl, tp, marginMode, fndLeverage, offsetMode, onSlChange, onTpChange, onMarginModeChange, onFndLeverageChange, onOffsetModeChange, onSignal, onToken, onDelete, onSettings, onStopPair, onForceUnlock,
  yomiSignal, yomiLastSignal, sempaiAlerts, makatsuSignals, isAdmin, onYomiSignal, onZanshinModeChange,
}: {
  pair: IxenPair
  centralized: boolean
  pairsCount: number
  submitting: number | 'global' | null
  sl: number
  tp: number
  marginMode: FprMarginMode
  fndLeverage: 10 | 15
  offsetMode: 'DYNAMIC' | 'STATIC'
  onSlChange: (v: number) => void
  onTpChange: (v: number) => void
  onMarginModeChange: (v: FprMarginMode) => void
  onFndLeverageChange: (v: 10 | 15) => void
  onOffsetModeChange: (v: 'DYNAMIC' | 'STATIC') => void
  onSignal: (pairId: number, dir: 'BUY' | 'SELL', centralized: boolean, confirmed: boolean, sl: number, tp: number, marginMode: FprMarginMode, fndLeverage: 10 | 15, offsetMode: 'DYNAMIC' | 'STATIC') => void
  onToken: (pair: IxenPair, type: 'PROP' | 'BROKER') => void
  onDelete: (pairId: number, name: string) => void
  onSettings: (pairId: number) => void
  onStopPair: (pairId: number, name: string) => void
  onForceUnlock: (pairId: number, name: string) => void
  yomiSignal?: YomiSignalResponse | null
  yomiLastSignal?: YomiLastSignalResponse | null
  sempaiAlerts?: SempaiAlertItem[]
  makatsuSignals?: MakatsuSignalItem[]
  isAdmin?: boolean
  onYomiSignal: (signal: YomiSignalResponse, pairId: number) => void
  onZanshinModeChange: (pairId: number, mode: ZanshinMode) => void
}) {
  const { pairsByPairId: zanshinPairsByPairId } = useZanshinData()
  const zanshinPair = zanshinPairsByPairId.get(pair.id) ?? null
  const [savingMode, setSavingMode] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const zMode: ZanshinMode = (pair.zanshin_mode === 'MANUAL' ? 'MANUAL' : 'AUTO')

  async function handleToggleZanshinMode(next: ZanshinMode) {
    if (next === zMode || savingMode) return
    setSavingMode(true)
    try {
      await zanshinService.setPairMode(pair.id, next)
      onZanshinModeChange(pair.id, next)
      toast.success(
        next === 'MANUAL'
          ? 'Modalita MANUAL attivata'
          : 'Modalita AUTO attivata',
        {
          description: next === 'MANUAL'
            ? 'Le decisioni Zanshin richiederanno la tua approvazione entro 60s.'
            : 'Le decisioni Zanshin saranno applicate automaticamente.',
        },
      )
    } catch (err: any) {
      toast.error('Impossibile cambiare modalita', { description: err?.detail ?? err?.message ?? '' })
    } finally {
      setSavingMode(false)
    }
  }

  const propState   = getEaState(pair.ea_prop)
  const brokerState = getEaState(pair.ea_broker)
  const propOk = pair.ea_prop.connected
  const brokerOk = pair.ea_broker.connected
  const bothOk = propOk && brokerOk
  const spread = pair.ea_prop.spread_points
  const spreadBlocked = spread !== null && spread > 90
  const spreadWarn = spread !== null && spread > 30 && spread <= 90
  const canTrade = propState === 'READY' && brokerState === 'READY' && !spreadBlocked && !pair.is_locked
  const isSubmitting = submitting === pair.id

  // ── Test Hedge: stato locale isolato per UX self-contained ───────────────
  type TestState = 'idle' | 'opening' | 'opened' | 'closing' | 'done' | 'failed'
  const [testState, setTestState] = useState<TestState>('idle')
  const [testMsg, setTestMsg] = useState<string>('')

  // ── Dismiss Yomi: nasconde il banner attivo finché non arriva un nuovo segnale ──
  // Memorizza l'evaluated_at del segnale ignorato. Se yomiSignal.evaluated_at coincide
  // con dismissedYomiAt, il banner ATTIVO non viene mostrato. Quando arriva un nuovo
  // segnale (evaluated_at diverso), il banner riappare automaticamente.
  const [dismissedYomiAt, setDismissedYomiAt] = useState<string | null>(null)
  const yomiActiveSuppressed = !!yomiSignal?.active
    && !!yomiSignal.evaluated_at
    && yomiSignal.evaluated_at === dismissedYomiAt

  async function handleTestHedge() {
    setTestState('opening')
    setTestMsg('Invio test hedge (0.01 lots)…')
    let signalId: number | null = null
    try {
      const res = await ixenSystemService.testHedge({
        ixen_pair_id: pair.id,
        sl_percent: sl,
        tp_percent: tp,
      })
      signalId = res.signal_id
      setTestMsg(`Apertura PROP+BROKER @ $${res.gold_price.toFixed(2)}…`)

      // Polling /signals fino a EXECUTED o timeout 30s
      const start = Date.now()
      let executed = false
      let propTicket: number | undefined, brokerTicket: number | undefined
      while (Date.now() - start < 30_000) {
        await new Promise(r => setTimeout(r, 1500))
        const data = await ixenSystemService.getSignals(20)
        const sig = data.signals.find(s => s.id === signalId)
        if (sig && sig.status === 'EXECUTED') {
          executed = true
          propTicket = (sig as any).prop_ticket
          brokerTicket = (sig as any).broker_ticket
          break
        }
        if (sig && sig.status === 'FAILED') {
          throw new Error(`Test fallito: ${(sig as any).notes ?? 'esecuzione EA fallita'}`)
        }
      }
      if (!executed) throw new Error('Timeout 30s — almeno un EA non ha confermato')

      setTestState('opened')
      setTestMsg(`✓ PROP #${propTicket ?? '?'} · BROKER #${brokerTicket ?? '?'} — chiusura…`)

      // Auto-close
      setTestState('closing')
      await new Promise(r => setTimeout(r, 500))  // micro-delay per evidenziare l'OK
      await ixenSystemService.closeTestHedge(signalId)

      setTestState('done')
      setTestMsg('✓ Test completato — entrambi gli EA funzionano')
      toast.success('Test hedge OK', { description: `PROP #${propTicket} · BROKER #${brokerTicket}` })
      setTimeout(() => { setTestState('idle'); setTestMsg('') }, 6000)
    } catch (err: any) {
      console.error('[TestHedge] error:', err)
      setTestState('failed')
      const detail = err?.response?.data?.detail || err?.message || 'errore sconosciuto'
      setTestMsg(`✗ ${detail}`)
      toast.error('Test hedge fallito', { description: detail })
      // Tenta chiusura difensiva se signal era stato creato
      if (signalId) {
        try { await ixenSystemService.closeTestHedge(signalId) } catch { /* silent */ }
      }
      setTimeout(() => { setTestState('idle'); setTestMsg('') }, 8000)
    }
  }
  const testBusy = testState === 'opening' || testState === 'opened' || testState === 'closing'
  // Cap autoritativi server-side (mirror IxenConsole.tsx): se mancanti (response legacy) → fallback statico
  const { slMax: providerSlMax, tpMax: providerTpMax } = getSlTpLimits(
    pair.prop_provider, pair.prop_phase, !!pair.control_mode,
  )
  const slMax = pair.sl_max_dynamic ?? calcDynamicSlMax(pair.ea_prop.equity, pair.prop_size, providerSlMax)
  const tpMax = pair.tp_max_dynamic ?? providerTpMax
  const slStatic = pair.sl_max_static ?? providerSlMax
  const tpStatic = pair.tp_max_static ?? providerTpMax
  const slLimited = slMax < slStatic - 1e-9   // ridotto dal buffer -6%
  const tpLimited = tpMax < tpStatic - 1e-9   // ridotto dal residuo target
  const targetReached = !!pair.payout_target_reached
  const isFprChallenge = pair.prop_provider === 'FPR' && pair.prop_phase === 'challenge'
  const isFndChallenge = pair.prop_provider === 'FND' && pair.prop_phase === 'challenge'

  // Stop globale operazioni manuali quando target payout raggiunto
  const canTradeFinal = canTrade && !targetReached

  return (
    <div className={`${card} flex flex-col gap-3`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-black text-white text-sm truncate">{pair.display_name}</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <PhaseBadge label={pair.prop_phase_label} />
            <span className="text-[10px] text-[#475569]">{pair.prop_provider_label} ${pair.prop_size.toLocaleString()}</span>
            <DailyBalanceBadge rs={pair.risk_state} />
            <RiskBadge rs={pair.risk_state} />
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className={`w-2 h-2 rounded-full ${propOk ? 'bg-green-400 shadow-[0_0_5px_#4ade80]' : 'bg-[#334155]'}`} />
          <div className={`w-2 h-2 rounded-full ${brokerOk ? 'bg-green-400 shadow-[0_0_5px_#4ade80]' : 'bg-[#334155]'}`} />
          <button
            onClick={() => onStopPair(pair.id, pair.display_name)}
            className="text-[10px] font-black px-2 py-0.5 rounded border border-red-800/60 text-red-500 hover:bg-red-900/30 hover:text-red-400 hover:border-red-700 transition-all ml-1"
            title="Stop emergenza — chiudi posizioni questa coppia"
          >■</button>
          <button
            onClick={() => onSettings(pair.id)}
            className="text-[#475569] hover:text-[#38bdf8] text-sm transition-colors"
            title="Impostazioni coppia"
          >⚙</button>
        </div>
      </div>

      {/* ═════════ SEZIONE 1 — CONTI COLLEGATI ═════════ */}
      <div className="space-y-2">
        <div className="text-[11px] font-bold text-[#64748b] uppercase tracking-wider">
          Conti collegati
          {pair.control_mode && (
            <span className="ml-2 text-[9px] font-black text-amber-400 bg-amber-500/10 border border-amber-500/40 rounded px-1.5 py-0.5 tracking-normal normal-case">
              Modalità Controllo
            </span>
          )}
        </div>
        <div className={`grid gap-2.5 ${pair.control_mode ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {/* PROP */}
          <div className="bg-[#0a0f1e] rounded-xl p-3 border border-[#1e3a5f]/60">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-white tracking-wide">MT5 PROP</span>
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                propState === 'READY'        ? 'bg-green-400 shadow-[0_0_5px_#4ade80]' :
                propState === 'NOT_READY'    ? 'bg-yellow-400 shadow-[0_0_5px_#facc15]' :
                                               'bg-[#334155]'
              }`} title={propState === 'NOT_READY' ? (NOT_READY_LABELS[pair.ea_prop.not_ready_reason ?? ''] ?? 'Non pronto') : ''} />
            </div>
            {propOk ? (
              <div className="space-y-1.5 text-[12px] text-[#cbd5e1]" style={{fontVariantNumeric:'tabular-nums'}}>
                <div className="flex items-center justify-between">
                  <span className="text-[#64748b]">Spread</span>
                  <SpreadBadge pts={pair.ea_prop.spread_points} />
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Equity</span>
                  <span className="font-mono font-bold text-white">${pair.ea_prop.equity?.toLocaleString('en-US', { maximumFractionDigits: 0 }) ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Pos. aperte</span>
                  <span className="font-mono font-bold text-white">{pair.ea_prop.open_positions ?? '—'}</span>
                </div>
                {propState === 'NOT_READY' && (
                  <div className="text-[10px] text-yellow-400 pt-1 border-t border-[#1e3a5f]/40">
                    {NOT_READY_LABELS[pair.ea_prop.not_ready_reason ?? ''] ?? pair.ea_prop.not_ready_reason ?? 'Non pronto'}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 mt-3">
                <p className="text-[12px] text-[#94a3b8] font-semibold">EA disconnesso</p>
                <button
                  onClick={() => onToken(pair, 'PROP')}
                  className="w-full text-[11px] text-[#38bdf8] underline font-bold py-1.5 border border-[#1e3a5f] rounded hover:bg-[#1e3a5f]/30 transition-colors"
                >
                  Mostra token →
                </button>
              </div>
            )}
          </div>

          {/* BROKER — nascosto in Modalità Controllo (prop-only) */}
          {!pair.control_mode && (
          <div className="bg-[#0a0f1e] rounded-xl p-3 border border-[#1e3a5f]/60">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-white tracking-wide">MT4 BROKER</span>
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                brokerState === 'READY'      ? 'bg-green-400 shadow-[0_0_5px_#4ade80]' :
                brokerState === 'NOT_READY'  ? 'bg-yellow-400 shadow-[0_0_5px_#facc15]' :
                                               'bg-[#334155]'
              }`} title={brokerState === 'NOT_READY' ? (NOT_READY_LABELS[pair.ea_broker.not_ready_reason ?? ''] ?? 'Non pronto') : ''} />
            </div>
            {brokerOk ? (
              <div className="space-y-1.5 text-[12px] text-[#cbd5e1]" style={{fontVariantNumeric:'tabular-nums'}}>
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Equity</span>
                  <span className="font-mono font-bold text-white">${pair.ea_broker.equity?.toLocaleString('en-US', { maximumFractionDigits: 0 }) ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Pos. aperte</span>
                  <span className="font-mono font-bold text-white">{pair.ea_broker.open_positions ?? '—'}</span>
                </div>
                {brokerState === 'NOT_READY' && (
                  <div className="text-[10px] text-yellow-400 pt-1 border-t border-[#1e3a5f]/40">
                    {NOT_READY_LABELS[pair.ea_broker.not_ready_reason ?? ''] ?? pair.ea_broker.not_ready_reason ?? 'Non pronto'}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 mt-3">
                <p className="text-[12px] text-[#94a3b8] font-semibold">EA disconnesso</p>
                <button
                  onClick={() => onToken(pair, 'BROKER')}
                  className="w-full text-[11px] text-[#38bdf8] underline font-bold py-1.5 border border-[#1e3a5f] rounded hover:bg-[#1e3a5f]/30 transition-colors"
                >
                  Mostra token →
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* ═════════ SEZIONE 2 — OPERAZIONE MANUALE ═════════ */}
      <div className={`space-y-3 bg-[#0a0f1e] rounded-xl p-4 border ${targetReached ? 'border-emerald-700/60' : 'border-[#1e3a5f]/60'}`}>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold text-[#64748b] uppercase tracking-wider">Operazione manuale</div>
          {targetReached && (
            <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-700/60">
              🎯 TARGET PAYOUT RAGGIUNTO
            </span>
          )}
        </div>

        {/* Banner target payout — blocco totale operazioni */}
        {targetReached && (
          <div className="rounded-lg border border-emerald-700/60 bg-emerald-900/20 px-3 py-2.5 text-[11px] text-emerald-200 leading-snug">
            <div className="font-black text-emerald-100 mb-0.5">Niente nuove operazioni su questa coppia.</div>
            Equity ${pair.current_equity?.toLocaleString('en-US', { maximumFractionDigits: 0 })} ≥ target ${pair.payout_target_equity?.toLocaleString('en-US', { maximumFractionDigits: 0 })}.
            Chiudi le posizioni aperte e richiedi il payout dal portale {pair.prop_provider_label}.
          </div>
        )}

        {/* FunderPro Challenge: selettore modalità margine */}
        {isFprChallenge && (
          <div className="grid grid-cols-3 gap-1.5">
            {(['prudente', 'standard', 'massimale'] as FprMarginMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => onMarginModeChange(mode)}
                className={`text-[11px] font-bold py-1.5 rounded-lg transition-all border ${
                  marginMode === mode
                    ? 'bg-[#1e3a5f] border-[#38bdf8] text-[#38bdf8]'
                    : 'bg-[#0d1529] border-[#1e3a5f] text-[#94a3b8] hover:border-[#38bdf8]/50'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
                <span className="block text-[9px] opacity-80 mt-0.5">{Math.round(FPR_MARGIN_MAP[mode] * 100)}%</span>
              </button>
            ))}
          </div>
        )}

        {/* FundedNext Challenge: selettore leva (Massimale 1:15 / Prudente 1:10) */}
        {isFndChallenge && (
          <div className="grid grid-cols-2 gap-1.5">
            {([15, 10] as Array<10 | 15>).map(lev => (
              <button
                key={lev}
                onClick={() => onFndLeverageChange(lev)}
                className={`text-[11px] font-bold py-1.5 rounded-lg transition-all border ${
                  fndLeverage === lev
                    ? 'bg-[#1e3a5f] border-[#38bdf8] text-[#38bdf8]'
                    : 'bg-[#0d1529] border-[#1e3a5f] text-[#94a3b8] hover:border-[#38bdf8]/50'
                }`}
              >
                {lev === 15 ? 'Massimale' : 'Prudente'}
                <span className="block text-[9px] opacity-80 mt-0.5">Leva 1:{lev}</span>
              </button>
            ))}
          </div>
        )}

        <SlTpSlider
          label="Stop Loss"
          value={Math.max(Math.min(sl, slMax), Math.min(0.01, slMax))}
          min={Math.min(0.01, slMax)} max={Math.max(slMax, 0.002)} step={0.0001}
          color="orange"
          onChange={onSlChange}
        />
        {slLimited && pair.buffer_dollar !== null && (
          <div className="text-[10px] text-amber-400/90 flex items-center gap-1">
            <span>⚠</span>
            <span>SL max ridotto dal buffer -6%: ${pair.buffer_dollar.toLocaleString('en-US', { maximumFractionDigits: 0 })} disponibili ({(slMax * 100).toFixed(2)}%)</span>
          </div>
        )}

        <SlTpSlider
          label="Take Profit"
          value={Math.min(tp, tpMax)}
          min={0.0001} max={Math.max(tpMax, 0.0001)} step={0.0001}
          color="green"
          onChange={onTpChange}
        />

        {/* Offset broker: scelta per-operazione DINAMICO / STATICO (reset a DINAMICO dopo ogni submit) */}
        {/* Nascosto in Modalità Controllo: nessun broker, nessun offset */}
        {!pair.control_mode && (
        <div className="space-y-1">
          <div className="text-[10px] text-[#94a3b8] uppercase font-bold tracking-wider">Offset Broker</div>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => onOffsetModeChange('DYNAMIC')}
              className={`py-1.5 rounded-md text-[11px] font-bold border transition-all ${
                offsetMode === 'DYNAMIC'
                  ? 'bg-[#1e3a5f] text-[#38bdf8] border-[#38bdf8]'
                  : 'bg-[#0d1529] text-[#94a3b8] border-[#475569] hover:border-[#64748b]'
              }`}
              title="Offset calcolato sullo spread PROP+BROKER (comportamento di default)"
            >
              DINAMICO
            </button>
            <button
              type="button"
              onClick={() => onOffsetModeChange('STATIC')}
              className={`py-1.5 rounded-md text-[11px] font-bold border transition-all ${
                offsetMode === 'STATIC'
                  ? 'bg-[#1e3a5f] text-[#38bdf8] border-[#38bdf8]'
                  : 'bg-[#0d1529] text-[#94a3b8] border-[#475569] hover:border-[#64748b]'
              }`}
              title="Offset fisso $0.30, bypassa la conferma spread elevato"
            >
              STATICO
            </button>
          </div>
          <div className="text-[10px] text-[#64748b] leading-tight">
            {offsetMode === 'DYNAMIC' ? 'Calcolato sullo spread' : 'Fisso $0.30'}
          </div>
        </div>
        )}

        {/* Spread alerts */}
        {spreadBlocked && (
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-2 text-[11px] text-red-400 font-bold text-center">
            ■ SPREAD {spread?.toFixed(0)}pt — BLOCCATO (&gt;90pt)
          </div>
        )}
        {spreadWarn && (
          <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-2 text-[11px] text-yellow-400 font-bold text-center">
            ⚠ Spread elevato {spread?.toFixed(0)}pt — richiesta conferma
          </div>
        )}

        {/* Test status overlay */}
        {testState !== 'idle' && (
          <div className={`rounded-lg border px-3 py-2 text-[11px] font-semibold leading-snug ${
            testState === 'failed'
              ? 'bg-red-900/20 border-red-800 text-red-300'
              : testState === 'done'
                ? 'bg-green-900/20 border-green-800 text-green-300'
                : 'bg-blue-900/20 border-blue-800 text-blue-300 animate-pulse'
          }`}>
            {testMsg}
          </div>
        )}

        {/* TEST · BUY · SELL — 3 colonne */}
        {(!centralized || pairsCount < 2) && (
          isSubmitting ? (
            <div className="rounded-lg border border-blue-700/60 bg-blue-900/20 px-3 py-3 flex items-start gap-2.5 animate-pulse">
              <span className="text-blue-400 text-base leading-none mt-0.5">⏳</span>
              <div>
                <p className="text-blue-300 font-black text-[12px] leading-tight">Apertura posizioni in corso…</p>
                <p className="text-blue-400/70 text-[11px] mt-0.5 leading-snug">
                  Controlla MT5 (PROP) e MT4 (BROKER): assicurati che entrambe le operazioni vengano eseguite prima di procedere.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={handleTestHedge}
                disabled={!canTradeFinal || isSubmitting || testBusy}
                title={
                  targetReached ? '🎯 Target payout raggiunto — chiudi posizioni e richiedi payout'
                  : !bothOk ? 'Connetti entrambi gli EA prima di testare'
                  : pair.is_locked ? 'Operazione già in corso'
                  : spreadBlocked ? 'Spread bloccato'
                  : 'Apre 0.01/0.01 lots su PROP+BROKER, verifica esecuzione e chiude subito'
                }
                className="py-2.5 rounded-lg font-bold text-[12px] bg-[#0d1529] border border-[#475569] text-[#cbd5e1] hover:border-[#38bdf8] hover:text-[#38bdf8] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                🧪 TEST
              </button>
              <button
                onClick={() => onSignal(pair.id, 'BUY', false, false, sl, tp, marginMode, fndLeverage, offsetMode)}
                disabled={!canTradeFinal}
                title={
                  targetReached ? '🎯 Target payout raggiunto — niente nuove operazioni'
                  : pair.is_locked ? 'Operazione già in corso — chiudi prima di aprirne una nuova'
                  : !bothOk ? 'EA non connesso'
                  : propState === 'NOT_READY' ? `EA PROP non pronto: ${NOT_READY_LABELS[pair.ea_prop.not_ready_reason ?? ''] ?? pair.ea_prop.not_ready_reason ?? ''} — abilita AutoTrading su MT5`
                  : brokerState === 'NOT_READY' ? `EA BROKER non pronto: ${NOT_READY_LABELS[pair.ea_broker.not_ready_reason ?? ''] ?? pair.ea_broker.not_ready_reason ?? ''} — abilita AutoTrading su MT4`
                  : spreadBlocked ? 'Spread bloccato'
                  : 'Invia segnale BUY'
                }
                className="py-2.5 rounded-lg font-black text-[13px] bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] border border-[#38bdf8] text-white shadow-[0_2px_10px_rgba(14,165,233,0.4)] hover:from-[#0284c7] hover:to-[#075985] disabled:opacity-30 disabled:cursor-not-allowed disabled:shadow-none transition-all"
              >
                ▲ BUY
              </button>
              <button
                onClick={() => onSignal(pair.id, 'SELL', false, false, sl, tp, marginMode, fndLeverage, offsetMode)}
                disabled={!canTradeFinal}
                title={
                  targetReached ? '🎯 Target payout raggiunto — niente nuove operazioni'
                  : pair.is_locked ? 'Operazione già in corso — chiudi prima di aprirne una nuova'
                  : !bothOk ? 'EA non connesso'
                  : propState === 'NOT_READY' ? `EA PROP non pronto: ${NOT_READY_LABELS[pair.ea_prop.not_ready_reason ?? ''] ?? pair.ea_prop.not_ready_reason ?? ''} — abilita AutoTrading su MT5`
                  : brokerState === 'NOT_READY' ? `EA BROKER non pronto: ${NOT_READY_LABELS[pair.ea_broker.not_ready_reason ?? ''] ?? pair.ea_broker.not_ready_reason ?? ''} — abilita AutoTrading su MT4`
                  : spreadBlocked ? 'Spread bloccato'
                  : 'Invia segnale SELL'
                }
                className="py-2.5 rounded-lg font-black text-[13px] bg-gradient-to-b from-rose-600 to-rose-800 border border-rose-500 text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)] hover:from-rose-700 hover:to-rose-900 disabled:opacity-30 disabled:cursor-not-allowed disabled:shadow-none transition-all"
              >
                ▼ SELL
              </button>
            </div>
          )
        )}

        {pair.is_locked && (
          <div className="space-y-2">
            <p className="text-[11px] text-center text-amber-400 font-semibold">
              ⚡ Operazione in corso — pulsanti disabilitati
            </p>
            {!bothOk && (
              <button
                onClick={() => onForceUnlock(pair.id, pair.display_name)}
                title="Sblocca lo stato lato server quando gli EA sono offline e i bottoni standard non funzionano. NON chiude le posizioni sui terminali."
                className="w-full py-2 rounded-lg border border-amber-700 bg-amber-900/20 text-amber-300 text-[11px] font-bold hover:bg-amber-900/40 transition-all"
              >
                🔓 Forza sblocco (EA offline)
              </button>
            )}
          </div>
        )}
        {!bothOk && !pair.is_locked && (
          <p className="text-[11px] text-center text-[#475569]">Connetti entrambi gli EA per operare</p>
        )}
      </div>

      {/* ═════════ SEZIONE 2.5 — ZANSHIN (stato posizione live + mode) ═════════ */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold text-[#64748b] uppercase tracking-wider">
            Zanshin {zanshinPair?.operation ? '— stato posizione' : ''}
          </div>
          {/* Toggle AUTO / MANUAL */}
          <div className="inline-flex rounded-md border border-[#1e3a5f] overflow-hidden">
            <button
              type="button"
              onClick={() => handleToggleZanshinMode('AUTO')}
              disabled={savingMode}
              title="Le decisioni Zanshin vengono applicate automaticamente"
              className={`px-2.5 py-1 text-[10px] font-black tracking-wider transition-colors ${
                zMode === 'AUTO'
                  ? 'bg-[#1e3a5f] text-[#38bdf8]'
                  : 'bg-transparent text-[#475569] hover:text-[#94a3b8]'
              } disabled:opacity-50`}
            >
              AUTO
            </button>
            <button
              type="button"
              onClick={() => handleToggleZanshinMode('MANUAL')}
              disabled={savingMode}
              title="Ogni decisione Zanshin richiede la tua approvazione entro 60s"
              className={`px-2.5 py-1 text-[10px] font-black tracking-wider border-l border-[#1e3a5f] transition-colors ${
                zMode === 'MANUAL'
                  ? 'bg-amber-900/40 text-amber-300'
                  : 'bg-transparent text-[#475569] hover:text-[#94a3b8]'
              } disabled:opacity-50`}
            >
              MANUAL
            </button>
          </div>
        </div>
        {zanshinPair && zanshinPair.operation && (
          <ZanshinPairCard pair={zanshinPair} />
        )}
        {zMode === 'MANUAL' && (
          <div className="text-[10px] text-amber-300/80 leading-snug">
            ⏳ Modalita MANUAL attiva: le proposte appariranno nel pannello "Proposte in attesa" in cima alla pagina. Hai 60s per approvare o rifiutare.
          </div>
        )}
      </div>

      {/* ═════════ SEZIONE 3 — MOTORI ZANSHIN (Yomi · Hyoshi · Sempai) ═════════ */}
      {/* In Modalità Controllo la coppia è prop-only: le card scanner e i
          dettagli tecnici non devono essere mostrati. Polling e motori restano
          invariati per le altre coppie. */}
      {!pair.control_mode && (!centralized || pairsCount < 2) && (
        <div className="space-y-2">
          {yomiActiveSuppressed && (
            <div className="flex items-center justify-end">
              <button
                onClick={() => setDismissedYomiAt(null)}
                className="text-[10px] font-bold text-[#38bdf8] hover:text-white transition-colors underline"
                title="Riapri il segnale Yomi ignorato"
              >
                Mostra Yomi
              </button>
            </div>
          )}

          {/* ── Riga 1: Yomi (trend follower, automatico) ── */}
          {/* Allineata alla card grande: se il live è idle ma esiste un ultimo
              segnale persistente ancora valido (ttl>0), la riga lo mostra. */}
          {(() => {
            const liveActive = !!yomiSignal?.active && !yomiActiveSuppressed
            const ls = yomiLastSignal?.signal
            const hasValidLast = !!(
              !liveActive &&
              !yomiActiveSuppressed &&
              yomiLastSignal?.active &&
              ls &&
              ls.ttl_seconds > 0 &&
              ls.prop_direction &&
              yomiSignal?.reason !== 'operation_already_open'
            )
            const active = liveActive || hasValidLast
            const dir = liveActive
              ? (yomiSignal?.prop_direction ?? null)
              : hasValidLast ? ls!.prop_direction : null
            const pct = liveActive
              ? yomiStrength(yomiSignal?.score).pct
              : hasValidLast ? yomiStrength(ls!.score).pct : 0
            const ttlTxt = hasValidLast
              ? `${Math.floor(ls!.ttl_seconds / 60)}m ${(ls!.ttl_seconds % 60).toString().padStart(2, '0')}s`
              : null
            const wr = yomiSignal?.session_quality?.win_rate_pct
            const refPrice = liveActive ? yomiSignal?.entry_price : (hasValidLast ? ls!.reference_price : null)
            const priceTxt = refPrice != null ? `@ ${refPrice.toFixed(2)}` : null
            const ctx = liveActive
              ? [yomiSignal?.signal_class, yomiSignal?.session_quality?.session_name, wr != null ? `WR ${Math.round(wr)}%` : null, priceTxt].filter(Boolean).join(' · ') || 'Segnale attivo'
              : hasValidLast
                ? [ls!.signal_class, ls!.scenario, ttlTxt ? `valido ${ttlTxt}` : null, priceTxt].filter(Boolean).join(' · ') || 'Ultimo segnale valido'
                : yomiActiveSuppressed ? 'Segnale ignorato'
                : (yomiSignal ? humanizeYomiReason(yomiSignal.reason) : 'In attesa di segnale')
            return (
              <EngineRow
                iconBg="bg-sky-500/15" iconText="text-sky-300" glyph="読"
                name="Yomi" role="Trend follower"
                active={active}
                direction={dir}
                scorePct={pct}
                barGradient="bg-gradient-to-r from-rose-500 via-amber-400 to-blue-500"
                subRight={active ? `${pct}/100` : '—/100'}
                context={ctx}
              />
            )
          })()}

          {/* ── Riga 2: Sempai (alert esterno, solo notifica — niente score) ── */}
          {(() => {
            const a = (sempaiAlerts && sempaiAlerts.length > 0) ? sempaiAlerts[0] : null
            const ageMin = a?.received_at ? (Date.now() - parseUtcDate(a.received_at).getTime()) / 60000 : Infinity
            const active = !!a && ageMin < 15
            const refPrice = a?.entry_price ?? null
            const priceTxt = refPrice != null && !isNaN(refPrice) ? ` · @ ${refPrice.toFixed(2)}` : ''
            return (
              <EngineRow
                iconBg="bg-amber-500/15" iconText="text-amber-300" glyph="先"
                name="Sempai" role="Alert esterno" badge="Notify"
                active={active}
                direction={a ? (a.direction === 'BUY' ? 'SELL' : 'BUY') : null}
                scorePct={null}
                subRight={a ? timeAgoIt(a.received_at) : 'nessun alert'}
                context={a ? `Fonte segnali esterna · ${a.direction}${priceTxt}` : 'Nessun alert ricevuto'}
              />
            )
          })()}

          {/* ── Riga 3: Makatsu — direzione mostrata = PROP invertita ── */}
          {(() => {
            const m = (makatsuSignals && makatsuSignals.length > 0) ? makatsuSignals[0] : null
            const ageMin = m?.received_at ? (Date.now() - parseUtcDate(m.received_at).getTime()) / 60000 : Infinity
            const active = !!m && ageMin < 15
            const refPrice = m?.entry_price ?? (m?.zone_low != null && m?.zone_high != null ? (m.zone_low + m.zone_high) / 2 : null)
            const priceTxt = refPrice != null && !isNaN(refPrice) ? ` · @ ${refPrice.toFixed(2)}` : ''
            return (
              <EngineRow
                iconBg="bg-rose-500/15" iconText="text-rose-300" glyph="魔"
                name="Makatsu" role="Segnali esterni" badge="Auto"
                active={active}
                direction={m ? (m.direction === 'BUY' ? 'SELL' : 'BUY') : null}
                scorePct={null}
                subRight={m ? timeAgoIt(m.received_at ?? '') : 'nessun segnale'}
                context={m ? `Segnale esterno · ${m.direction}${priceTxt}` : 'Nessun segnale ricevuto'}
              />
            )
          })()}

          {/* ── Dettagli tecnici (collapsible) — pannelli completi, admin-only ── */}
          {isAdmin && (
            <>
              <button
                onClick={() => setShowDetails(v => !v)}
                className="w-full flex items-center justify-center gap-1.5 text-[10px] font-bold text-[#64748b] hover:text-[#94a3b8] uppercase tracking-wider py-1 transition-colors"
              >
                Dettagli tecnici
                <span className={`transition-transform ${showDetails ? 'rotate-180' : ''}`}>▾</span>
              </button>

              {showDetails && (
                <div className="space-y-3 pt-1">
                  {yomiActiveSuppressed ? (
                    <div className="rounded-xl border border-[#1e3a5f]/60 bg-[#0a0f1e] px-3 py-3 text-[12px] text-[#94a3b8] text-center">
                      Segnale Yomi ignorato — in attesa di un nuovo aggiornamento.
                    </div>
                  ) : (
                    <YomiBanner
                      signal={yomiSignal ?? null}
                      lastSignal={yomiLastSignal ?? null}
                      pairId={pair.id}
                      onUse={onYomiSignal}
                      onDismiss={(evaluatedAt) => setDismissedYomiAt(evaluatedAt ?? null)}
                    />
                  )}
                  <SempaiDetail alerts={sempaiAlerts ?? []} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Pagina principale ────────────────────────────────────────────────────────

interface ConfirmModalData {
  pairId: number; pairName: string; direction: 'BUY' | 'SELL'; centralized: boolean
  spreadPoints: number; brokerSpreadPoints: number | null; offsetPrice: number; message: string
}

interface YomiConfirmData {
  pairId: number
  pairName: string
  direction: 'BUY' | 'SELL'      // PROP direction
  generatedAt: string             // ISO — anti-replay
  fprMarginMode: FprMarginMode
  fndLeverage: 10 | 15
  isFprPair: boolean
  isFndPair: boolean
  slPercent: number
  tpPercent: number
  // Campi enriched (possono essere null se backend non li ha calcolati)
  entryPrice: number | null
  lotsProp: number | null
  lotsBroker: number | null
  slPropPrice: number | null
  tpPropPrice: number | null
  slDistanceUsd: number | null
  tpDistanceUsd: number | null
  signalClass: string | null
  score: number | null
}

interface TokenModalData {
  pair: IxenPair; type: 'PROP' | 'BROKER'; token: string
}

export const IxenSystem = () => {
  const isAdmin = useAuthStore(s => s.isAdmin)
  const [pairs, setPairs] = useState<IxenPair[]>([])
  const [signals, setSignals] = useState<IxenSignal[]>([])
  const [loading, setLoading] = useState(true)
  const [centralized, setCentralized] = useState(false)
  const [submitting, setSubmitting] = useState<number | 'global' | null>(null)
  const [pairParams, setPairParams] = useState<Record<number, { sl: number; tp: number; marginMode: FprMarginMode; fndLeverage: 10 | 15; offsetMode?: 'DYNAMIC' | 'STATIC' }>>({})

  function getPairSl(pair: IxenPair): number {
    if (pairParams[pair.id]?.sl !== undefined) return pairParams[pair.id].sl
    // Default 1,2%, mai oltre il cap dinamico della coppia
    return Math.min(0.012, pair.sl_max_dynamic ?? 0.012)
  }
  function getPairTp(pair: IxenPair): number {
    if (pairParams[pair.id]?.tp !== undefined) return pairParams[pair.id].tp
    // Default = min(cap statico, cap dinamico server-side) → mai oltre target residuo
    const tpStatic = getSlTpLimits(
      pair.prop_provider, pair.prop_phase, !!pair.control_mode,
    ).tpMax
    return Math.min(tpStatic, pair.tp_max_dynamic ?? tpStatic)
  }
  function getPairMarginMode(pair: IxenPair): FprMarginMode {
    return pairParams[pair.id]?.marginMode ?? 'massimale'
  }
  function getPairFndLeverage(pair: IxenPair): 10 | 15 {
    return pairParams[pair.id]?.fndLeverage ?? 10
  }
  function getPairOffsetMode(pair: IxenPair): 'DYNAMIC' | 'STATIC' {
    return pairParams[pair.id]?.offsetMode ?? 'DYNAMIC'
  }
  function setPairSl(pairId: number, v: number) {
    setPairParams(prev => ({ ...prev, [pairId]: { ...prev[pairId], sl: v, marginMode: prev[pairId]?.marginMode ?? 'massimale', fndLeverage: prev[pairId]?.fndLeverage ?? 10 } }))
  }
  function setPairTp(pairId: number, v: number) {
    setPairParams(prev => ({ ...prev, [pairId]: { ...prev[pairId], tp: v, marginMode: prev[pairId]?.marginMode ?? 'massimale', fndLeverage: prev[pairId]?.fndLeverage ?? 10 } }))
  }
  function setPairMarginMode(pairId: number, v: FprMarginMode) {
    setPairParams(prev => ({ ...prev, [pairId]: { ...prev[pairId], marginMode: v, fndLeverage: prev[pairId]?.fndLeverage ?? 10 } }))
  }
  function setPairFndLeverage(pairId: number, v: 10 | 15) {
    setPairParams(prev => ({ ...prev, [pairId]: { ...prev[pairId], fndLeverage: v, marginMode: prev[pairId]?.marginMode ?? 'massimale' } }))
  }
  function setPairOffsetMode(pairId: number, v: 'DYNAMIC' | 'STATIC') {
    setPairParams(prev => ({ ...prev, [pairId]: { ...prev[pairId], offsetMode: v, marginMode: prev[pairId]?.marginMode ?? 'massimale', fndLeverage: prev[pairId]?.fndLeverage ?? 10 } as any }))
  }
  // Reset volatile: rimuove la chiave offsetMode per tornare al default DYNAMIC.
  // Chiamato dopo ogni submit success (NON in NEEDS_CONFIRMATION).
  function resetPairOffsetMode(pairId: number) {
    setPairParams(prev => {
      const cur = prev[pairId]
      if (!cur || cur.offsetMode === undefined) return prev
      const { offsetMode: _omit, ...rest } = cur
      return { ...prev, [pairId]: rest }
    })
  }

  const [showCreate, setShowCreate] = useState(false)
  const [confirmModal, setConfirmModal] = useState<ConfirmModalData | null>(null)
  const [yomiConfirm, setYomiConfirm] = useState<YomiConfirmData | null>(null)
  const [stopConfirm, setStopConfirm] = useState(false)
  const [stopPairConfirm, setStopPairConfirm] = useState<{ id: number; name: string } | null>(null)
  // ── Manual rollback opt-in (Opzione 4) ──────────────────────────────────
  const { t } = useTranslation()
  const [manualRollback, setManualRollback] = useState<boolean>(false)
  const [manualRollbackBusy, setManualRollbackBusy] = useState<boolean>(false)
  const [manualRecoveries, setManualRecoveries] = useState<any[]>([])
  useEffect(() => {
    ixenSystemService.getManualRollback()
      .then(r => setManualRollback(!!r.enabled))
      .catch(() => { /* silenzioso: il toggle resta false */ })
  }, [])
  // Fetch dedicato MANUAL_RECOVERY (indipendente dal dashboard last-30)
  // → garantisce che il banner non nasconda mai recovery aperte.
  const fetchManualRecoveries = useCallback(() => {
    ixenSystemService.listManualRecoveries()
      .then(r => setManualRecoveries(r.recoveries || []))
      .catch(() => { /* silenzioso */ })
  }, [])
  useEffect(() => {
    fetchManualRecoveries()
    const id = setInterval(fetchManualRecoveries, 10000)
    return () => clearInterval(id)
  }, [fetchManualRecoveries])
  async function handleToggleManualRollback(next: boolean) {
    setManualRollbackBusy(true)
    try {
      const r = await ixenSystemService.setManualRollback(next)
      setManualRollback(!!r.enabled)
    } catch {
      toast.error(t('manualRollback.errorToggle'))
    } finally {
      setManualRollbackBusy(false)
    }
  }
  async function handleDismissRecovery(signalId: number) {
    try {
      await ixenSystemService.dismissRecovery(signalId)
      toast.success(t('manualRollback.dismissed'))
      setManualRecoveries(prev => prev.filter(r => r.id !== signalId))
      fetchManualRecoveries()
      fetchData()
    } catch {
      toast.error(t('manualRollback.errorDismiss'))
    }
  }
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; name: string } | null>(null)
  const [tokenModal, setTokenModal] = useState<TokenModalData | null>(null)
  const [settingsModal, setSettingsModal] = useState<number | null>(null)

  // ── Feed status ────────────────────────────────────────────────────────────
  const [feedStatus, setFeedStatus] = useState<{
    is_ready: boolean; warming_up: boolean; is_connected: boolean
    live_bid: number; spread_points: number; price_age_seconds: number; tick_count: number
  } | null>(null)

  useEffect(() => {
    const poll = async () => {
      try {
        const h = await ixenSystemService.getMarketHealth()
        setFeedStatus(h)
      } catch { /* feed non disponibile */ }
    }
    poll()
    // Stato feed (globale): 15s è impercettibile per il badge ma riduce di 3×
    // il carico di questo polling, il più frequente per ogni utente collegato.
    const t = setInterval(poll, 15000)
    return () => clearInterval(t)
  }, [])

  // ── MarketContext globale (admin-only) ─────────────────────────────────────
  const [marketCtx, setMarketCtx] = useState<MarketContextResponse | null>(null)

  useEffect(() => {
    if (!isAdmin) return
    const poll = async () => {
      try {
        const ctx = await ixenSystemService.getMarketContext()
        setMarketCtx(ctx)
      } catch { /* silent */ }
    }
    poll()
    const t = setInterval(poll, 30000)
    return () => clearInterval(t)
  }, [isAdmin])

  // ── Yomi signals (admin-only) ──────────────────────────────────────────────
  const [yomiSignals, setYomiSignals] = useState<Record<number, YomiSignalResponse>>({})
  // Signal Persistence — endpoint ASSET-scoped (XAUUSD): unico stato globale,
  // chiamato 1 sola volta per polling tick, condiviso da tutte le pair.
  const [yomiLastSignal, setYomiLastSignal] = useState<YomiLastSignalResponse | null>(null)

  const [sempaiAlerts, setSempaiAlerts] = useState<SempaiAlertItem[]>([])
  const [makatsuSignals, setMakatsuSignals] = useState<MakatsuSignalItem[]>([])

  // Polling Sempai (alert esterni) + Makatsu, interval 30s. Aperti a tutti.
  useEffect(() => {
    if (pairs.length === 0) return
    const fetchSempai = async () => {
      try {
        const a = await ixenSystemService.getSempaiRecentAlerts(5)
        setSempaiAlerts(a)
      } catch { /* silent */ }
    }
    const fetchMakatsu = async () => {
      try {
        const m = await ixenSystemService.getMakatsuRecentSignals(5)
        setMakatsuSignals(m)
      } catch { /* silent */ }
    }
    fetchSempai(); fetchMakatsu()
    const t = setInterval(() => { fetchSempai(); fetchMakatsu() }, 30000)
    return () => clearInterval(t)
  }, [pairs.length])

  // Ref ai valori correnti: l'effetto di polling Yomi si registra UNA sola
  // volta (deps [pairs.length]) e legge pairs/pairParams aggiornati via ref,
  // così non si ricrea ad ogni refresh di `pairs` (~12s) né ad ogni cambio di
  // `pairParams` — evita il fan-out di richieste duplicate verso /yomi/{id}.
  const yomiPairsRef = useRef(pairs)
  yomiPairsRef.current = pairs
  const yomiPairParamsRef = useRef(pairParams)
  yomiPairParamsRef.current = pairParams
  // Guardia anti-sovrapposizione: se un ciclo fetchYomi è ancora in volo
  // (backend lento), salta il tick successivo invece di accavallare N richieste.
  const yomiFetchInFlightRef = useRef(false)

  useEffect(() => {
    if (pairs.length === 0) return
    const fetchYomi = async () => {
      if (yomiFetchInFlightRef.current) return
      yomiFetchInFlightRef.current = true
      try {
        const curPairs = yomiPairsRef.current
        const curParams = yomiPairParamsRef.current
        const results: Record<number, YomiSignalResponse> = {}
        await Promise.allSettled(
          curPairs.map(async (p) => {
            try {
              // Margin mode / leva correnti dalla ref: la preview entry/lotti/SL/TP
              // combacia con quello che _submit_signal_impl userà all'esecuzione.
              const mm = curParams[p.id]?.marginMode ?? 'massimale'
              const lev = curParams[p.id]?.fndLeverage ?? 10
              const sig = await ixenSystemService.getYomiSignal(p.id, mm, lev)
              results[p.id] = sig
            } catch { /* silent */ }
          })
        )
        setYomiSignals(prev => ({ ...prev, ...results }))
        // Last-signal: chiamata UNA sola volta (asset-scoped, condivisa).
        try {
          const last = await ixenSystemService.getYomiLastSignal('XAUUSD', 15)
          setYomiLastSignal(last)
        } catch { /* silent */ }
      } finally {
        yomiFetchInFlightRef.current = false
      }
    }
    fetchYomi()
    const t = setInterval(fetchYomi, 30000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairs.length])

  // Tracking dei cambi di zanshin_mode recenti per pair: protegge dalla race
  // condition polling vs PATCH. Un fetch in volo lanciato PRIMA del PATCH può
  // arrivare DOPO il commit e sovrascrivere il valore appena scelto dall'utente.
  // SOLDI VERI: meglio "appiccicoso" sul lato client per N secondi che un flip
  // silenzioso AUTO/MANUAL non voluto.
  const recentZanshinModeRef = useRef<Map<number, { mode: ZanshinMode; ts: number }>>(new Map())
  const ZANSHIN_MODE_STICKY_MS = 15000 // 15s = ~2 cicli polling (polling 8s)

  const fetchData = useCallback(async () => {
    try {
      const data = await ixenSystemService.getDashboard(30)
      // Merge zanshin_mode: se l'utente ha cambiato mode da meno di ZANSHIN_MODE_STICKY_MS,
      // teniamo il valore locale finché il server non lo conferma. Quando server == local,
      // l'entry viene rimossa (PATCH propagato, ok ad accettare server).
      const now = Date.now()
      const stickyMap = recentZanshinModeRef.current
      const mergedPairs = data.pairs.map(serverPair => {
        const sticky = stickyMap.get(serverPair.id)
        if (!sticky) return serverPair
        if (now - sticky.ts > ZANSHIN_MODE_STICKY_MS) {
          stickyMap.delete(serverPair.id)
          return serverPair
        }
        if (serverPair.zanshin_mode === sticky.mode) {
          // server confermato: cleanup entry, prendi server
          stickyMap.delete(serverPair.id)
          return serverPair
        }
        // server ancora indietro (race o PATCH non ancora committato): tieni locale
        return { ...serverPair, zanshin_mode: sticky.mode }
      })
      setPairs(mergedPairs)
      setSignals(data.signals)
    } catch { /* silent */ } finally { setLoading(false) }
  }, [])

  // Esposto al PairCard via onZanshinModeChange: registra il cambio + aggiorna state.
  const handleZanshinModeChangeLocal = useCallback((pairId: number, mode: ZanshinMode) => {
    recentZanshinModeRef.current.set(pairId, { mode, ts: Date.now() })
    setPairs(prev => prev.map(p => p.id === pairId ? { ...p, zanshin_mode: mode } : p))
  }, [])

  useEffect(() => {
    fetchData()
    const t = setInterval(fetchData, 12000)
    return () => clearInterval(t)
  }, [fetchData])

  const connectedCount = pairs.reduce((acc, p) => acc + (p.ea_prop.connected ? 1 : 0) + (p.ea_broker.connected ? 1 : 0), 0)

  async function handleSignal(pairId: number, dir: 'BUY' | 'SELL', isCentralized: boolean, confirmed = false, sl?: number, tp?: number, marginMode?: FprMarginMode, fndLeverage?: 10 | 15, offsetMode?: 'DYNAMIC' | 'STATIC') {
    setSubmitting(isCentralized ? 'global' : pairId)
    try {
      const pairForCap = pairs.find(p => p.id === pairId)
      const slCap = pairForCap?.sl_max_dynamic
      const slClamped = (sl != null && slCap != null && Number.isFinite(slCap) && slCap > 0)
        ? Math.min(sl, slCap)
        : sl
      const tpCap = pairForCap?.tp_max_dynamic
      const tpClamped = (tp != null && tpCap != null && Number.isFinite(tpCap) && tpCap > 0)
        ? Math.min(tp, tpCap)
        : tp
      const res = await ixenSystemService.submitSignal({
        ixen_pair_id: pairId, direction: dir, centralized: isCentralized, confirmed,
        sl_percent: slClamped, tp_percent: tpClamped,
        funder_pro_margin_mode: marginMode ?? 'massimale',
        fnd_leverage: fndLeverage ?? 10,
        offset_mode: offsetMode ?? 'DYNAMIC',
      })
      if (res.status === 'NEEDS_CONFIRMATION') {
        const pair = pairs.find(p => p.id === pairId)
        setConfirmModal({
          pairId, pairName: pair?.display_name ?? `Coppia #${pairId}`, direction: dir, centralized: isCentralized,
          spreadPoints: res.spread_points ?? 0, brokerSpreadPoints: res.broker_spread_points ?? null,
          offsetPrice: res.offset_price ?? 0.30, message: res.message,
        })
      } else {
        const clampNotes: string[] = []
        if (res.sl_clamped_from != null && res.sl_clamped_to != null) {
          clampNotes.push(`SL ridotto da ${(res.sl_clamped_from*100).toFixed(2)}% a ${(res.sl_clamped_to*100).toFixed(2)}% (vicino al limite -6%)`)
        }
        if (res.tp_clamped_from != null && res.tp_clamped_to != null) {
          clampNotes.push(`TP ridotto da ${(res.tp_clamped_from*100).toFixed(2)}% a ${(res.tp_clamped_to*100).toFixed(2)}% (vicino al target payout)`)
        }
        const desc = clampNotes.length > 0 ? `${res.message} — ${clampNotes.join(' · ')}` : res.message
        toast.success(`Segnale ${dir} inviato`, { description: desc, duration: clampNotes.length > 0 ? 8000 : 4000 })
        // Reset offset mode a DYNAMIC dopo submit riuscito (comportamento volatile come SL/TP).
        resetPairOffsetMode(pairId)
        fetchData()
      }
    } catch (err: any) {
      console.error('[IxenSystem] submitSignal error:', err)
      const detail = err?.message && typeof err.message === 'string' && err.message.trim().length > 0
        ? err.message
        : 'Errore invio segnale. Riprova tra qualche istante.'
      toast.error(`Segnale ${dir} rifiutato`, { description: detail, duration: 8000 })
    } finally { setSubmitting(null) }
  }

  async function handleCentralizedSignal(dir: 'BUY' | 'SELL') {
    // Filtra le coppie con target payout già raggiunto: il backend le rifiuterebbe
    // comunque (HTTP 400) ma evitiamo toast di errore inutili e tempo sprecato.
    const tradable = pairs.filter(p => !p.payout_target_reached)
    const skipped = pairs.length - tradable.length
    if (tradable.length === 0) {
      toast.error('🎯 Target payout raggiunto su tutte le coppie', {
        description: 'Chiudi posizioni e richiedi i payout dai portali prop firm',
      })
      return
    }
    if (skipped > 0) {
      toast.info(`Segnale inviato a ${tradable.length}/${pairs.length} coppie`, {
        description: `${skipped} coppia${skipped === 1 ? '' : 'e'} con target payout già raggiunto, escluse`,
      })
    }
    for (const pair of tradable) {
      await handleSignal(pair.id, dir, true, false, getPairSl(pair), getPairTp(pair), getPairMarginMode(pair), getPairFndLeverage(pair), getPairOffsetMode(pair))
    }
  }

  // ── Yomi manual execute: apre il modale di conferma con countdown 30s ──
  // L'esecuzione vera avviene in handleYomiConfirm → ixenSystemService.executeYomiSignal,
  // che ri-legge il segnale lato backend e applica anti-replay su generated_at.
  function handleYomiSignal(signal: YomiSignalResponse, pairId: number) {
    if (!signal.active || !signal.prop_direction || !signal.generated_at) {
      toast.error('Segnale Yomi non disponibile')
      return
    }
    const pair = pairs.find(p => p.id === pairId)
    if (!pair) return
    setYomiConfirm({
      pairId,
      pairName: pair.display_name,
      direction: signal.prop_direction,
      generatedAt: signal.generated_at,
      fprMarginMode: getPairMarginMode(pair),
      fndLeverage: getPairFndLeverage(pair),
      isFprPair: pair.prop_provider === 'FPR',
      isFndPair: pair.prop_provider === 'FND',
      slPercent: signal.sl_percent ?? 0,
      tpPercent: signal.tp_percent ?? 0,
      entryPrice: signal.entry_price ?? null,
      lotsProp: signal.lots_prop ?? null,
      lotsBroker: signal.lots_broker ?? null,
      slPropPrice: signal.sl_prop_price ?? null,
      tpPropPrice: signal.tp_prop_price ?? null,
      slDistanceUsd: signal.sl_distance_usd ?? null,
      tpDistanceUsd: signal.tp_distance_usd ?? null,
      signalClass: signal.signal_class ?? null,
      score: signal.score ?? null,
    })
  }

  async function handleYomiConfirm() {
    if (!yomiConfirm) return
    const data = yomiConfirm
    setYomiConfirm(null)
    setSubmitting(data.pairId)
    try {
      const res = await ixenSystemService.executeYomiSignal(data.pairId, {
        expected_direction: data.direction,
        generated_at: data.generatedAt,
        fpr_margin_mode: data.fprMarginMode,
        fnd_leverage: data.fndLeverage,
      })
      const clampNotes: string[] = []
      if (res.sl_clamped_from != null && res.sl_clamped_to != null) {
        clampNotes.push(`SL ridotto a ${(res.sl_clamped_to*100).toFixed(2)}%`)
      }
      if (res.tp_clamped_from != null && res.tp_clamped_to != null) {
        clampNotes.push(`TP ridotto a ${(res.tp_clamped_to*100).toFixed(2)}%`)
      }
      const desc = clampNotes.length > 0 ? `${res.message} — ${clampNotes.join(' · ')}` : res.message
      toast.success(`Segnale Yomi ${data.direction} inviato`, {
        description: desc,
        duration: clampNotes.length > 0 ? 8000 : 4000,
      })
      fetchData()
    } catch (err: any) {
      console.error('[Yomi] executeYomiSignal error:', err)
      const detail = err?.message && typeof err.message === 'string' && err.message.trim().length > 0
        ? err.message
        : 'Errore esecuzione segnale Yomi. Riprova tra qualche istante.'
      toast.error(`Yomi ${data.direction} rifiutato`, { description: detail, duration: 8000 })
    } finally {
      setSubmitting(null)
    }
  }

  async function handleConfirmSpread() {
    if (!confirmModal) return
    const pair = pairs.find(p => p.id === confirmModal.pairId)
    setConfirmModal(null)
    await handleSignal(
      confirmModal.pairId, confirmModal.direction, confirmModal.centralized, true,
      pair ? getPairSl(pair) : undefined, pair ? getPairTp(pair) : undefined,
      pair ? getPairMarginMode(pair) : undefined,
      pair ? getPairFndLeverage(pair) : undefined,
      pair ? getPairOffsetMode(pair) : undefined,
    )
  }

  async function handleEmergencyStop() {
    setStopConfirm(false)
    try {
      const res = await ixenSystemService.emergencyStop()
      toast.error('Stop emergenza inviato', { description: res.message })
      fetchData()
    } catch { toast.error('Errore stop emergenza') }
  }

  async function handleStopPair(pairId: number) {
    setStopPairConfirm(null)
    try {
      const res = await ixenSystemService.stopPair(pairId)
      toast.error('Stop inviato', { description: res.message })
      fetchData()
    } catch { toast.error('Errore stop coppia') }
  }

  async function handleForceUnlock(pairId: number, name: string) {
    const confirmed = window.confirm(
      `⚠️ FORZA SBLOCCO — ${name || 'Pair'}\n\n` +
      `Questo sblocca lo stato lato server senza chiudere le posizioni sui terminali MT4/MT5.\n\n` +
      `Usa SOLO se gli EA sono offline da tempo e il bottone "Chiudi" non funziona.\n\n` +
      `Se hai posizioni aperte sui terminali, devi chiuderle MANUALMENTE.\n\n` +
      `Procedere?`
    )
    if (!confirmed) return
    try {
      const res = await ixenSystemService.forceUnlockPair(pairId)
      toast.success('Pair sbloccato', {
        description: `${res.closed_signals} segnali + ${res.closed_hedge_ops} operazioni chiuse lato server. Verifica MT4/MT5.`,
      })
      fetchData()
    } catch (err: any) {
      toast.error('Errore sblocco forzato', { description: err?.detail ?? err?.message ?? '' })
    }
  }

  async function handleDeletePair(pairId: number) {
    try {
      await ixenSystemService.deletePair(pairId)
      setPairs(prev => prev.filter(p => p.id !== pairId))
      toast.success('Coppia eliminata')
    } catch { toast.error('Errore eliminazione coppia') } finally { setDeleteConfirm(null) }
  }

  async function handleShowToken(pair: IxenPair, type: 'PROP' | 'BROKER') {
    const token = type === 'PROP' ? pair.ea_prop.ea_token : pair.ea_broker.ea_token
    if (token) {
      setTokenModal({ pair, type, token })
    } else {
      try {
        const res = await ixenSystemService.resetEaToken(pair.id, type)
        setTokenModal({ pair, type, token: res.ea_token })
        fetchData()
      } catch { toast.error('Errore recupero token') }
    }
  }

  async function handleResetToken() {
    if (!tokenModal) return
    try {
      const res = await ixenSystemService.resetEaToken(tokenModal.pair.id, tokenModal.type)
      setTokenModal(prev => prev ? { ...prev, token: res.ea_token } : null)
      toast.success('Token rigenerato — aggiorna il parametro nell\'EA')
      fetchData()
    } catch { toast.error('Errore rigenera token') }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0f1e' }}>
        <div className="text-[#38bdf8] text-sm animate-pulse">Caricamento iXen System...</div>
      </div>
    )
  }

  return (
    <SubscriptionBlocker>
      <div className="p-4 min-h-screen" style={{ background: '#0a0f1e', color: '#e2e8f0' }}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black tracking-tight text-white">
                ⚡ <span className="text-[#38bdf8]">iXen</span> System
              </h1>
              {/* Feed status badge */}
              {feedStatus === null ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#1e3a5f]/40 text-[#475569] border border-[#1e3a5f]/40">
                  Feed —
                </span>
              ) : feedStatus.is_ready ? (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-400 border border-emerald-700/50 cursor-default"
                  title={`XAUUSD bid ${feedStatus.live_bid.toFixed(2)} • spread ${feedStatus.spread_points.toFixed(1)}pt • ${feedStatus.tick_count} tick • età ${feedStatus.price_age_seconds.toFixed(1)}s`}
                >
                  ● Feed LIVE
                </span>
              ) : feedStatus.warming_up ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400 border border-amber-700/50 animate-pulse">
                  ◌ Feed warmup…
                </span>
              ) : feedStatus.is_connected ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-400 border border-amber-700/50">
                  ◌ Feed sync…
                </span>
              ) : (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-900/30 text-red-400 border border-red-800/50 cursor-default"
                  title="Feed MetaAPI non connesso — il prezzo verrà preso dall'EA"
                >
                  ○ Feed off
                </span>
              )}
            </div>
            <p className="text-[11px] text-[#475569] mt-0.5">
              {pairs.length === 0
                ? 'Nessuna coppia configurata — aggiungi la prima coppia EA'
                : `${pairs.length} coppi${pairs.length > 1 ? 'e' : 'a'} attiv${pairs.length > 1 ? 'e' : 'a'} • ${connectedCount}/${pairs.length * 2} EA connessi`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#1e3a5f] bg-[#0a0f1e]/60"
              title={t('manualRollback.tooltip')}
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#94a3b8]">
                {t('manualRollback.toggleLabel')}
              </span>
              <Switch
                checked={manualRollback}
                onCheckedChange={handleToggleManualRollback}
                disabled={manualRollbackBusy}
              />
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 bg-[#1e3a5f]/60 border border-[#38bdf8]/30 text-[#38bdf8] hover:bg-[#1e3a5f] font-bold text-xs px-3 py-2 rounded-lg transition-all"
            >
              + Nuova coppia EA
            </button>
            <button
              onClick={() => setStopConfirm(true)}
              disabled={pairs.length === 0}
              className="flex items-center gap-1.5 bg-red-900/30 border border-red-700 text-red-400 hover:bg-red-900/50 font-black text-xs px-3 py-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ■ STOP
            </button>
          </div>
        </div>

        {/* ── Banner Manual Recovery ─────────────────────────────────── */}
        {(() => {
          const recovery = manualRecoveries
          if (recovery.length === 0) return null
          return (
            <div className="mb-4 rounded-xl border-2 border-red-700 bg-red-900/20 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-400 text-lg">⚠</span>
                <span className="text-red-300 font-black text-sm uppercase tracking-wider">
                  {t('manualRollback.bannerTitle')} ({recovery.length})
                </span>
              </div>
              <p className="text-[11px] text-red-200/80 mb-3 leading-snug">
                {t('manualRollback.bannerDesc')}
              </p>
              <div className="space-y-2">
                {recovery.map((s: any) => {
                  const sideConfirmed = s.prop_ticket && s.prop_ticket > 0 ? 'PROP' : 'BROKER'
                  const ticket = sideConfirmed === 'PROP' ? s.prop_ticket : s.broker_ticket
                  let delaySec: number | null = null
                  if (s.expires_at) {
                    delaySec = Math.max(0, Math.round((Date.now() - parseUtcDate(s.expires_at).getTime()) / 1000))
                  }
                  return (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-3 rounded-lg bg-red-950/40 border border-red-800/60 px-3 py-2"
                    >
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
                        <span className="font-black text-red-200">{s.pair_name || `#${s.ixen_pair_id}`}</span>
                        <span className="text-red-300">
                          <span className="text-red-400/70">{s.direction}</span>
                        </span>
                        <span className="text-red-300">
                          <span className="text-red-400/70">{t('manualRollback.rowSide')}:</span>{' '}
                          <span className="font-bold">{sideConfirmed}</span>
                        </span>
                        <span className="text-red-300">
                          <span className="text-red-400/70">{t('manualRollback.rowTicket')}:</span>{' '}
                          <span className="font-mono font-bold">#{ticket}</span>
                        </span>
                        {delaySec !== null && (
                          <span className="text-red-300">
                            <span className="text-red-400/70">{t('manualRollback.rowDelay')}:</span>{' '}
                            <span className="font-bold">{delaySec}{t('manualRollback.rowSeconds')}</span>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDismissRecovery(s.id)}
                        className="shrink-0 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-lg border border-red-700 bg-red-900/40 text-red-200 hover:bg-red-900/70 transition-all"
                      >
                        {t('manualRollback.dismiss')}
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {/* ── Toggle segnale centralizzato (solo 2+ coppie) ── */}
        {pairs.length >= 2 && (
          <div className={`${card} mb-4 flex items-center justify-between gap-4`}>
            <div>
              <div className={sectionTitle}>Segnale centralizzato</div>
              <p className="text-[11px] text-[#64748b]">
                {centralized ? 'BUY/SELL viene inviato a tutte le coppie contemporaneamente' : 'Ogni coppia riceve il suo segnale indipendente'}
              </p>
            </div>
            <Switch checked={centralized} onCheckedChange={setCentralized} />
          </div>
        )}

        {/* ── Bottoni globali (solo con toggle attivo) ── */}
        {centralized && pairs.length >= 2 && (() => {
          const targetReachedCount = pairs.filter(p => p.payout_target_reached).length
          const tradablePairs = pairs.length - targetReachedCount
          const allBlocked = tradablePairs === 0
          const someBlocked = targetReachedCount > 0 && !allBlocked
          return (
            <div className={`${card} mb-4`}>
              <div className="flex items-center justify-between mb-3">
                <div className={sectionTitle.replace('mb-3', '')}>
                  Segnale globale — {tradablePairs}/{pairs.length} coppie
                </div>
                {someBlocked && (
                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-700/60">
                    🎯 {targetReachedCount} TARGET RAGGIUNTO
                  </span>
                )}
              </div>
              {allBlocked && (
                <div className="rounded-lg border border-emerald-700/60 bg-emerald-900/20 px-3 py-2.5 text-[11px] text-emerald-200 leading-snug mb-3">
                  <div className="font-black text-emerald-100 mb-0.5">Tutte le coppie hanno raggiunto il target payout.</div>
                  Chiudi le posizioni aperte e richiedi i payout dai portali prop firm — niente nuove operazioni.
                </div>
              )}
              {someBlocked && (
                <p className="text-[11px] text-emerald-300/90 mb-3">
                  Il segnale verrà inviato solo alle {tradablePairs} coppie senza target raggiunto.
                </p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => handleCentralizedSignal('BUY')}
                  disabled={submitting === 'global' || allBlocked}
                  title={allBlocked ? '🎯 Target payout raggiunto su tutte le coppie' : 'Invia BUY a tutte le coppie tradabili'}
                  className="flex-1 py-3 rounded-lg font-black text-sm bg-gradient-to-b from-[#0ea5e9] to-[#0369a1] border border-[#38bdf8] text-white shadow-[0_2px_10px_rgba(14,165,233,0.4)] hover:from-[#0284c7] hover:to-[#075985] disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed transition-all"
                >
                  ▲ BUY — TUTTE LE COPPIE
                </button>
                <button
                  onClick={() => handleCentralizedSignal('SELL')}
                  disabled={submitting === 'global' || allBlocked}
                  title={allBlocked ? '🎯 Target payout raggiunto su tutte le coppie' : 'Invia SELL a tutte le coppie tradabili'}
                  className="flex-1 py-3 rounded-lg font-black text-sm bg-gradient-to-b from-rose-600 to-rose-800 border border-rose-500 text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)] hover:from-rose-700 hover:to-rose-900 disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed transition-all"
                >
                  ▼ SELL — TUTTE LE COPPIE
                </button>
              </div>
            </div>
          )
        })()}

        {/* ── Stato vuoto ── */}
        {pairs.length === 0 && (
          <div className={`${card} text-center py-14`}>
            <div className="text-5xl mb-4">⚡</div>
            <p className="text-[#94a3b8] font-bold mb-2 text-base">Nessuna coppia EA configurata</p>
            <p className="text-[#475569] text-sm mb-6 max-w-sm mx-auto">
              Aggiungi la prima coppia inserendo le credenziali del conto PROP (MT5) e del conto BROKER (MT4).
              Nessun account MetaAPI viene creato.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="bg-[#1e3a5f] border border-[#38bdf8]/40 text-[#38bdf8] hover:bg-[#1e4a7f] font-bold text-sm px-6 py-2.5 rounded-lg transition-all"
            >
              + Aggiungi prima coppia EA
            </button>
          </div>
        )}

        {/* ── Proposte Zanshin in attesa (visibile solo se ce ne sono) ── */}
        {pairs.length > 0 && <ZanshinApprovalsPanel />}

        {/* ── MarketContext globale (collassabile, default chiuso) — admin-only ── */}
        {isAdmin && pairs.length > 0 && <MarketContextPanel ctx={marketCtx} />}

        {/* ── Cards coppie ── */}
        {pairs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            {pairs.map(pair => (
              <PairCard
                key={pair.id}
                pair={pair}
                centralized={centralized}
                pairsCount={pairs.length}
                submitting={submitting}
                sl={getPairSl(pair)}
                tp={getPairTp(pair)}
                marginMode={getPairMarginMode(pair)}
                fndLeverage={getPairFndLeverage(pair)}
                offsetMode={getPairOffsetMode(pair)}
                onSlChange={v => setPairSl(pair.id, v)}
                onTpChange={v => setPairTp(pair.id, v)}
                onMarginModeChange={v => setPairMarginMode(pair.id, v)}
                onFndLeverageChange={v => setPairFndLeverage(pair.id, v)}
                onOffsetModeChange={v => setPairOffsetMode(pair.id, v)}
                onSignal={handleSignal}
                onToken={handleShowToken}
                onDelete={(id, name) => setDeleteConfirm({ id, name })}
                onSettings={id => setSettingsModal(id)}
                onStopPair={(id, name) => setStopPairConfirm({ id, name })}
                onForceUnlock={handleForceUnlock}
                yomiSignal={yomiSignals[pair.id]}
                yomiLastSignal={yomiLastSignal}
                sempaiAlerts={sempaiAlerts}
                makatsuSignals={makatsuSignals}
                isAdmin={isAdmin}
                onYomiSignal={handleYomiSignal}
                onZanshinModeChange={handleZanshinModeChangeLocal}
              />
            ))}
          </div>
        )}

        {/* ── Log segnali ── */}
        {signals.length > 0 && (
          <div className={card}>
            <div className={sectionTitle}>Log operazioni</div>
            <div className="space-y-0 max-h-56 overflow-y-auto">
              {signals.map(sig => (
                <div key={sig.id} className="flex items-center gap-3 py-1.5 border-b border-[#1e3a5f]/30 last:border-0 text-xs">
                  <span className="text-[#475569] font-mono text-[10px] w-16 flex-shrink-0">
                    {parseUtcDate(sig.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <span className="text-[#64748b] flex-shrink-0 w-28 truncate text-[10px]">{sig.pair_name ?? `#${sig.ixen_pair_id}`}</span>
                  <span className={`font-black text-[10px] w-10 flex-shrink-0 ${sig.direction === 'BUY' ? 'text-green-400' : sig.direction === 'SELL' ? 'text-red-400' : 'text-orange-400'}`}>
                    {sig.direction}
                  </span>
                  <SignalStatusBadge status={sig.status} />
                  {sig.notes && <span className="text-[#475569] text-[9px] truncate flex-1">{sig.notes.replace(/jifu/gi, 'sempai')}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Modali ── */}

        {showCreate && (
          <CreatePairModal
            onClose={() => setShowCreate(false)}
            onCreated={(p) => { setPairs(prev => [...prev, p]); setShowCreate(false) }}
          />
        )}

        {tokenModal && (
          <TokenModal
            pair={tokenModal.pair}
            type={tokenModal.type}
            token={tokenModal.token}
            onReset={handleResetToken}
            onClose={() => setTokenModal(null)}
          />
        )}

        {confirmModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0d1529] border border-yellow-700 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <div className="text-yellow-400 font-black text-base mb-1">⚠ Spread Elevato</div>
              <p className="text-[#94a3b8] text-sm mb-4">{confirmModal.message}</p>
              <div className="bg-[#0a0f1e] rounded-lg p-3 mb-4 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-[#64748b]">Spread PROP</span>
                  <span className="text-yellow-400 font-bold">{confirmModal.spreadPoints.toFixed(0)} pt</span>
                </div>
                {confirmModal.brokerSpreadPoints !== null && (
                  <div className="flex justify-between">
                    <span className="text-[#64748b]">Spread BROKER</span>
                    <span className="text-[#94a3b8] font-bold">{confirmModal.brokerSpreadPoints?.toFixed(0)} pt</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-[#1e3a5f] pt-1 mt-1">
                  <span className="text-[#64748b]">Offset dinamico</span>
                  <span className="text-[#38bdf8] font-black">${confirmModal.offsetPrice.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setConfirmModal(null)} className="flex-1 py-2.5 rounded-lg border border-[#1e3a5f] text-[#64748b] text-sm font-bold hover:bg-[#1e3a5f]/30 transition-all">Annulla</button>
                <button onClick={handleConfirmSpread} className="flex-1 py-2.5 rounded-lg bg-yellow-900/40 border border-yellow-700 text-yellow-400 text-sm font-black hover:bg-yellow-900/60 transition-all">Conferma {confirmModal.direction}</button>
              </div>
            </div>
          </div>
        )}

        {yomiConfirm && (
          <YomiConfirmModal
            data={yomiConfirm}
            onConfirm={handleYomiConfirm}
            onCancel={() => setYomiConfirm(null)}
          />
        )}

        {stopConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0d1529] border border-red-700 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <div className="text-red-400 font-black text-base mb-1">■ Stop Emergenza Globale</div>
              <p className="text-[#94a3b8] text-sm mb-6">
                Segnale di chiusura immediata inviato a <strong className="text-white">{pairs.length} coppi{pairs.length > 1 ? 'e' : 'a'}</strong>. Confermare?
              </p>
              <div className="flex gap-3">
                <button onClick={() => setStopConfirm(false)} className="flex-1 py-2.5 rounded-lg border border-[#1e3a5f] text-[#64748b] text-sm font-bold hover:bg-[#1e3a5f]/30 transition-all">Annulla</button>
                <button onClick={handleEmergencyStop} className="flex-1 py-2.5 rounded-lg bg-red-900/40 border border-red-700 text-red-400 text-sm font-black hover:bg-red-900/60 transition-all">Conferma Stop</button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0d1529] border border-red-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <div className="text-[#94a3b8] font-black text-base mb-1">Elimina coppia EA</div>
              <p className="text-[#64748b] text-sm mb-6">
                Eliminare <strong className="text-white">{deleteConfirm.name}</strong>? I token EA associati verranno revocati.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-lg border border-[#1e3a5f] text-[#64748b] text-sm font-bold hover:bg-[#1e3a5f]/30 transition-all">Annulla</button>
                <button onClick={() => handleDeletePair(deleteConfirm.id)} className="flex-1 py-2.5 rounded-lg bg-red-900/40 border border-red-700 text-red-400 text-sm font-black hover:bg-red-900/60 transition-all">Elimina</button>
              </div>
            </div>
          </div>
        )}

        {stopPairConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0d1529] border border-red-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <div className="text-red-400 font-black text-base mb-1">■ Stop — {stopPairConfirm.name}</div>
              <p className="text-[#94a3b8] text-sm mb-6">
                Invia segnale di chiusura immediata alla coppia <strong className="text-white">{stopPairConfirm.name}</strong>. Gli EA chiuderanno tutte le posizioni aperte.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setStopPairConfirm(null)} className="flex-1 py-2.5 rounded-lg border border-[#1e3a5f] text-[#64748b] text-sm font-bold hover:bg-[#1e3a5f]/30 transition-all">Annulla</button>
                <button onClick={() => handleStopPair(stopPairConfirm.id)} className="flex-1 py-2.5 rounded-lg bg-red-900/40 border border-red-700 text-red-400 text-sm font-black hover:bg-red-900/60 transition-all">Conferma Stop</button>
              </div>
            </div>
          </div>
        )}

        {settingsModal !== null && (
          <PairManagementModal
            pairId={settingsModal}
            onClose={() => setSettingsModal(null)}
            onUpdated={updatedPair => {
              setPairs(prev => prev.map(p => p.id === updatedPair.id ? { ...p, ...updatedPair } : p))
            }}
            onDeleted={pairId => {
              setPairs(prev => prev.filter(p => p.id !== pairId))
              setSettingsModal(null)
            }}
          />
        )}

      </div>
    </SubscriptionBlocker>
  )
}
